import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
  AiProviderStatus,
  AvailableThinker,
  CapabilityCatalog,
  CapabilityPolicy,
  CatalogStatus,
  CatalogThinker,
  GroupOption,
  LocalThinker,
  LocalThinkerModels,
  SelectionAction,
  ThinkerShare,
} from "~/api/aiAdminTypes";
import type {
  CanopyFindResult,
  CanopyNodeQuery,
  CanopyNodeView,
  CanopyScope,
  DebugTree,
  KnowledgeDebugReport,
} from "~/api/canopyTypes";
import type {
  ActivityQuery,
  AiCallsQuery,
  AiUsageQuery,
  ApiClient,
  CapabilityPolicyDraft,
  CollectionPatch,
  DeliveryQuery,
  DocumentPatch,
  DocumentQuery,
  FlowRunRequest,
  LocalThinkerDraft,
  PersonaPatch,
  ReportQuery,
  RunApproval,
  RunControl,
  RunQuery,
  ScheduledTaskDraft,
  SculptRunRequest,
  SelectionActionDraft,
  ThinkerBinding,
  ThinkerDraft,
  ThinkerShareDraft,
  WorkspaceThinkerDraft,
} from "~/api/client";
import type {
  Comment,
  CommentDraft,
  CommentListQuery,
  CommentPage,
  DocumentChatMessage,
  ReactionSummary,
} from "~/api/commentTypes";
import type {
  BdiRun,
  ContextConfig,
  ContextItemDraft,
  ContextPack,
  ConversationSearch,
  ConversationsOverview,
  DecisionAction,
  DecisionDraft,
  Conversation as DocConversation,
  ConversationSummary as DocConversationSummary,
  DocumentAiSettings,
  DocumentThinkerResolution,
  MessageDraft,
  Outcomes,
  PromptRunDraft,
  RevisePatch,
  SourceTrace,
  StartConversationDraft,
  ThinkerDisposition,
  TruthDraft,
  UsageRollup,
} from "~/api/conversationTypes";
import type {
  AiActionDecision,
  AiActionRequest,
  DeriveOutcome,
  EditorV2Commit,
  EditorV2Lane,
  EditorV2OptIn,
  IdeDispatch,
  MentionSuggestions,
  QuoteSnapshot,
  RefineEvent,
  RefineRequest,
  ScopedActionQuery,
  TransformOutcome,
  TransformRequest,
} from "~/api/editorTypes";
import {
  NotFoundError,
  SculptConfirmRequiredError,
  SculptDisabledError,
  SculptError,
} from "~/api/errors";
import type {
  GitConnection,
  GitConnectionDraft,
  GitConnectionList,
  GitConnectionPatch,
  GitFile,
  GitHubAppStatus,
  GitHubInstallation,
  GitHubRepository,
  GitIngestReport,
  GitProbe,
  GitTree,
} from "~/api/gitTypes";
import type {
  AuthIdentity,
  AuthPolicy,
  AuthRole,
  AuthTeam,
  AuthTeamMember,
  AuthTeamMembership,
  EntityAbilities,
  GrantLevel,
  GroupMemberRole,
  GroupMembership,
  MemberInvite,
  MemberRole,
  Organization,
  Project,
  ProjectDraft,
  ProjectGroupMembership,
  ProjectMembership,
  ProjectPatch,
  ProjectStatus,
  SourceApproval,
  SourceApprovalStatus,
  SourcePolicy,
  Webhook,
  WebhookDelivery,
  WebhookDraft,
} from "~/api/governanceTypes";
import type {
  ConversationDetail,
  ConversationDraft,
  ConversationSummary,
  DispatchDraft,
  DispatchOutput,
  DocSyncLink,
  DocSyncLinkFlags,
  DocSyncStatePage,
  IdeGrantActivity,
  IdeGrantApproval,
  IdeGrantCandidate,
  IdeGrantList,
  IdeSessionHistory,
  IdeSessionsPage,
  TalkThread,
  WizardCapabilities,
  WizardPreflight,
} from "~/api/ideTypes";
import type { NotifyInput, TaskizeInput } from "~/api/intentTypes";
import {
  isLiveRun,
  type KnowledgeRefreshResult,
  type KnowledgeRefreshTarget,
  type KnowledgeRun,
  type KnowledgeRunState,
  type KnowledgeStatus,
} from "~/api/knowledgeTypes";
import { leaveApp } from "~/api/leaveApp";
import type {
  CollectionPermission,
  ExportFormat,
  PinList,
  RecentSearch,
  SearchParams,
  SearchResults,
  ShareLink,
  ShareLinkPatch,
  StarList,
  StarTarget,
} from "~/api/libraryTypes";
import type {
  MailActivity,
  MailConnection,
  MailConnectionPatch,
  MailImportAs,
  MailImportResult,
  MailInboxMessage,
  MailProviderName,
  MailProviders,
  MailSendInput,
  MailSyncReport,
} from "~/api/mailTypes";
import type {
  ArtifactContent,
  BillingUsage,
  DigestTaskDraft,
  Directive,
  DocumentTask,
  DocumentTaskInput,
  DocumentTaskPatch,
  FlowHealthSettings,
  FlowHealthSettingsPatch,
  FlowTaskDraft,
  IdeSession,
  JumpRequest,
  LedgerPage,
  LedgerReport,
  Nudge,
  NudgeTarget,
  PublishOptions,
  QuickWhenResult,
  RouteSurface,
  RunArtifact,
  RunDetail,
  RunEfficiency,
  RunTree,
  TaskBoard,
} from "~/api/opsTypes";
import { useApi } from "~/api/provider";
import type {
  ActivityPage,
  Agent,
  AiCallsPage,
  AiUsage,
  AnswerTelemetry,
  ApiKey,
  Collection,
  CvInterview,
  CvInterviewState,
  Dispatch,
  Divergence,
  Document,
  Expert,
  FlowDefinitionResult,
  FlowDetail,
  FlowList,
  FlowNodeHistory,
  FlowStatus,
  FlowVersion,
  GrantComment,
  GrantRead,
  Group,
  Member,
  MergeAudit,
  MergeCommitResult,
  MergeDecision,
  MergePartDiff,
  MergeSession,
  Notification,
  NotificationChannel,
  NotificationPreferences,
  PortfolioEntry,
  ProfileCv,
  ProfilePatch,
  ProfileSearchPage,
  ProfileSearchQuery,
  PublicProfileCv,
  QueueDigest,
  Revision,
  RevisionPart,
  Run,
  RunLogLine,
  RunStats,
  RunStep,
  ScheduledTask,
  SculptLatest,
  SculptOutcome,
  Session,
  Share,
  ShareGrant,
  ShareGrantDraft,
  ShareGrantPatch,
  SkillLevel,
  Viewer,
  WisdomCoverage,
  WisdomFilters,
  WisdomNeighborhood,
  WisdomNode,
  WisdomOverview,
  WisdomProvenance,
  WisdomSearchResult,
  WisdomTopDown,
  Workspace,
} from "~/api/types";

/**
 * One place that names every cache entry. Invalidation bugs are usually key-shape bugs, so
 * the shapes live together where they can be compared at a glance.
 */
export const queryKeys = {
  session: ["session"] as const,
  viewer: ["viewer"] as const,
  workspaces: ["workspaces"] as const,
  currentWorkspace: ["workspaces", "current"] as const,
  collections: ["collections"] as const,
  /** W10 §8 — the archived list is a different question, so a different entry. */
  collectionsWithArchived: ["collections", "with-archived"] as const,
  collectionPeople: (id: string) => ["collections", "people", id] as const,
  collectionGroups: (id: string) => ["collections", "groups", id] as const,
  collection: (id: string) => ["collections", "detail", id] as const,
  documents: (query?: DocumentQuery) =>
    [
      "documents",
      query?.status ?? "published",
      query?.collectionId ?? "",
      // `null` means "top-level only" and must not alias with undefined's "no filter".
      query?.parentDocumentId === undefined ? "" : String(query.parentDocumentId),
      query?.query ?? "",
      // Part of the key because two callers with the same key must fetch the same data —
      // the sidebar tree (limit 100) and a scene (default limit) must not share an entry.
      query?.limit ?? 0,
    ] as const,
  document: (id: string) => ["documents", "detail", id] as const,
  revisions: (id: string) => ["documents", "revisions", id] as const,
  // Under the documents root on purpose: applying/reverting a sculpt changes the document,
  // so the two must always invalidate together.
  sculptLatest: (id: string) => ["documents", "sculpt", id] as const,
  search: (query: string) => ["search", query] as const,
  /** W10 §9 — under the documents root: templatizing a document changes both lists. */
  templates: ["documents", "templates"] as const,
  /**
   * W10 §6 — the filtered search. Every filter is part of the key: two searches that differ by
   * a filter are two different questions and must not share a cache entry.
   */
  searchResults: (params: SearchParams) =>
    [
      "search",
      "filtered",
      params.query,
      params.collectionId ?? "",
      params.userId ?? "",
      params.dateFilter ?? "",
      (params.statusFilter ?? []).join(","),
      params.limit ?? 0,
    ] as const,
  recentSearches: ["search", "recent"] as const,
  members: ["members"] as const,
  groups: ["groups"] as const,
  apiKeys: ["apiKeys"] as const,
  shares: ["shares"] as const,
  /** W10 §2 — the viewer's stars. One key: the list IS the "is this starred?" answer. */
  stars: ["stars"] as const,
  /**
   * W10 §3 — pins, keyed by scope. `""` is the HOME scope (the pins that belong to no
   * collection), which the platform treats as a real filter rather than "all" — so it must be
   * its own cache entry, not a hole in the key.
   */
  pins: (collectionId?: string) => ["pins", collectionId ?? ""] as const,
  /**
   * W10 §4 — the share LINK for one document. Under the `shares` root so revoking a link from
   * the settings list also refreshes the dialog, and vice versa.
   */
  documentShare: (documentId: string) => ["shares", "document", documentId] as const,
  publicShare: (id: string) => ["public", "share", id] as const,
  publicProfile: (slug: string) => ["public", "profile", slug] as const,
  agents: ["automation", "agents"] as const,
  flows: (includeArchived: boolean) => ["automation", "flows", includeArchived] as const,
  flow: (slug: string) => ["automation", "flows", "detail", slug] as const,
  entityFlows: (kind: string, id: string) => ["automation", "flows", "entity", kind, id] as const,
  flowVersions: (slug: string) => ["automation", "flows", "versions", slug] as const,
  /** The head is `"head"`, not `undefined`: a key with a hole in it collides with itself. */
  flowDefinition: (slug: string, version?: number) =>
    ["automation", "flows", "definition", slug, version ?? "head"] as const,
  flowNodeHistory: (slug: string, nodeId: string, limit: number) =>
    ["automation", "flows", "node-history", slug, nodeId, limit] as const,
  runs: (query?: RunQuery) =>
    [
      "automation",
      "runs",
      query?.status ?? "",
      query?.kind ?? "",
      query?.activeOnly === true,
    ] as const,
  run: (id: string) => ["automation", "runs", "detail", id] as const,
  runSteps: (id: string) => ["automation", "runs", "steps", id] as const,
  runTree: (id: string) => ["automation", "runs", "tree", id] as const,
  runLogs: (id: string) => ["automation", "runs", "logs", id] as const,
  runStats: ["automation", "runs", "stats"] as const,
  tasks: ["automation", "tasks"] as const,
  dispatches: (activeOnly: boolean) => ["automation", "dispatches", activeOnly] as const,
  activity: (query?: ActivityQuery) =>
    ["automation", "activity", query?.auditLog === true, query?.offset ?? 0] as const,
  // 0 / "" mean "server default" — part of the key so two windows never share an entry.
  aiUsage: (days: number) => ["ai", "usage", days] as const,
  aiCalls: (query?: AiCallsQuery) =>
    ["ai", "calls", query?.days ?? 0, query?.limit ?? 0, query?.lane ?? ""] as const,
  aiQueueDigest: ["ai", "queue-digest"] as const,
  // ── AI administration (W1) ─────────────────────────────────────────────────
  thinkers: ["thinkers"] as const,
  thinkerCatalog: ["thinkers", "catalog"] as const,
  thinkerCatalogStatus: ["thinkers", "catalog", "status"] as const,
  persona: (id: string) => ["thinkers", "persona", id] as const,
  // The suggestions are per document, so the key carries the document — two documents must
  // never share an entry and serve each other's people.
  suggestedThinkers: (documentId: string) => ["thinkers", "suggested", documentId] as const,
  thinkerShares: (id: string) => ["thinkers", "shares", id] as const,
  thinkerGroups: ["thinkers", "groups"] as const,
  localThinkers: ["thinkers", "local"] as const,
  // The installed-model set changes whenever somebody runs `ollama pull`, and it is per host.
  localThinkerModels: (baseUrl: string) => ["thinkers", "local", "models", baseUrl] as const,
  capabilityPolicies: ["capabilities", "policies"] as const,
  capabilityCatalog: ["capabilities", "catalog"] as const,
  aiActions: ["ai-actions"] as const,
  aiProviderStatus: ["ai-actions", "provider-status"] as const,
  // ── Automation operations (W2) ───────────────────────────────────────────
  taskBoard: (activeOnly: boolean) => ["automation", "board", activeOnly] as const,
  ledgerActions: (runId: string | undefined, failedOnly: boolean) =>
    ["automation", "ledger", "actions", runId ?? "__all__", failedOnly] as const,
  ledgerReport: (runId: string | undefined, groupBy: string) =>
    ["automation", "ledger", "report", runId ?? "__all__", groupBy] as const,
  ledgerEfficiency: (runId: string) => ["automation", "ledger", "efficiency", runId] as const,
  runDetail: (id: string) => ["automation", "runs", "detail", "full", id] as const,
  runArtifacts: (id: string) => ["automation", "runs", "artifacts", id] as const,
  runArtifactContent: (id: string, path: string) =>
    ["automation", "runs", "artifacts", id, path] as const,
  directives: (flowId: string) => ["automation", "directives", flowId] as const,
  ideSessions: ["automation", "ide-sessions"] as const,
  health: (days: number) => ["health", "usage", days] as const,
  healthSettings: ["health", "settings"] as const,
  deliveries: (query: DeliveryQuery) =>
    ["health", "deliveries", query.channel ?? "", query.status ?? "", query.offset ?? 0] as const,
  documentTasks: (query: { documentId?: string; assignedToMe?: boolean; includeDone?: boolean }) =>
    [
      "documentTasks",
      query.documentId ?? "",
      query.assignedToMe === true,
      query.includeDone === true,
    ] as const,
  nudges: (documentId?: string) => ["nudges", documentId ?? ""] as const,
  // ── W5: projects, governance, people writes ──────────────────────────────
  projects: (query?: { status?: ProjectStatus; groupId?: string; limit?: number }) =>
    ["projects", query?.status ?? "", query?.groupId ?? "", query?.limit ?? 0] as const,
  projectsMine: ["projects", "mine"] as const,
  // Sorted so two callers requesting the same set in a different order share the entry.
  projectAbilities: (ids: string[]) =>
    ["projects", "abilities", [...ids].sort().join(",")] as const,
  projectPeople: (id: string) => ["projects", "people", id] as const,
  projectGroups: (id: string) => ["projects", "groups", id] as const,
  groupMembers: (id: string) => ["groups", "members", id] as const,
  authIdentity: ["governance", "auth", "identity"] as const,
  authMyTeams: ["governance", "auth", "my-teams"] as const,
  authTeams: ["governance", "auth", "teams"] as const,
  authTeamMembers: (teamId: string) => ["governance", "auth", "teams", teamId, "members"] as const,
  authRoles: ["governance", "auth", "roles"] as const,
  authPolicies: ["governance", "auth", "policies"] as const,
  organizations: ["governance", "organizations"] as const,
  sourcePolicy: ["governance", "source-policy"] as const,
  sourceApprovals: (status?: string) => ["governance", "source-approvals", status ?? ""] as const,
  webhooks: ["governance", "webhooks"] as const,
  gitConnections: (scope: string) => ["git", "connections", scope] as const,
  gitTree: (id: string, path: string) => ["git", "tree", id, path] as const,
  gitFile: (id: string, path: string) => ["git", "file", id, path] as const,
  webhookDeliveries: (id: string) => ["governance", "webhooks", "deliveries", id] as const,
  // ── W4: connected editor windows, grants, doc sync, conversations ────────
  /**
   * ONE key for the window list, shared by the console, the target picker and the sync
   * section. React Query's dedupe is what replaces kms's reference-counted 60 s timer: three
   * consumers, one request, and none of the bookkeeping that made `stopPolling` a bug farm.
   */
  ideSessionsPage: (includeRevoked: boolean) => ["ide", "sessions", includeRevoked] as const,
  myIdeWindows: ["ide", "sessions", "mine-everywhere"] as const,
  ideSessionHistory: (sessionKey: string) => ["ide", "sessions", "history", sessionKey] as const,
  ideGrants: ["ide", "grants"] as const,
  ideGrantActivity: (grantId: string) => ["ide", "grants", "activity", grantId] as const,
  ideGrantCandidates: ["ide", "grants", "candidates"] as const,
  dispatch: (id: string) => ["automation", "dispatches", "one", id] as const,
  talkThreads: ["automation", "dispatches", "threads"] as const,
  dispatchOutput: (id: string) => ["automation", "dispatches", "output", id] as const,
  docSyncLinks: ["docsync", "links"] as const,
  docSyncState: (linkId: string) => ["docsync", "state", linkId] as const,
  conversations: ["conversations"] as const,
  conversation: (id: string) => ["conversations", id] as const,
  // W8 — the document AI layer shares the `conversations` root so one invalidation covers
  // both faces of the same aggregate.
  documentConversations: (documentId: string, search: ConversationSearch = {}) =>
    [
      "conversations",
      "document",
      documentId,
      search.search ?? "",
      search.resolved ?? "",
      search.priority ?? "",
      search.limit ?? 50,
    ] as const,
  conversationDetail: (id: string) => ["conversations", "detail", id] as const,
  conversationsRecentlyDeleted: ["conversations", "recently-deleted"] as const,
  conversationsOverview: (limit: number) => ["conversations", "overview", limit] as const,
  conversationContext: (id: string) => ["conversations", "context", id] as const,
  conversationContextConfig: (id: string) => ["conversations", "context-config", id] as const,
  conversationThinkers: (id: string) => ["conversations", "thinkers", id] as const,
  conversationOutcomes: (id: string) => ["conversations", "outcomes", id] as const,
  conversationRuns: (id: string) => ["conversations", "runs", id] as const,
  conversationUsage: (id: string) => ["conversations", "usage", id] as const,
  conversationSourceTraces: (documentId: string) =>
    ["conversations", "source-traces", documentId] as const,
  documentAiSettings: (documentId: string) => ["conversations", "ai-settings", documentId] as const,
  documentThinker: (documentId: string, collectionId: string) =>
    ["conversations", "document-thinker", documentId, collectionId] as const,
  // W8 S9/S10 — their own roots: a comment write must not invalidate the transcript, and
  // the chat poll must not be swept away by a conversation mutation.
  documentChat: (documentId: string) => ["document-chat", documentId] as const,
  comments: (query: CommentListQuery) =>
    [
      "comments",
      query.documentId ?? "",
      query.parentCommentId ?? "",
      query.collectionId ?? "",
      (query.statusFilter ?? []).join(","),
      query.sort ?? "createdAt",
      query.direction ?? "DESC",
      query.limit ?? 100,
    ] as const,
  commentReactions: (commentId: string) => ["comments", "reactions", commentId] as const,
  wizardCapabilities: ["wizard", "capabilities"] as const,
  wizardPreflight: ["wizard", "preflight"] as const,
  // W6 — the v2 editing lane.
  editorV2Lane: (documentId: string) => ["editor-v2", "lane", documentId] as const,
  collaborationToken: ["editor-v2", "collaboration-token"] as const,
  scopedAiActions: (query: ScopedActionQuery) =>
    [
      "ai-actions",
      "scoped",
      query.teamId ?? "",
      query.projectId ?? "",
      query.documentId ?? "",
    ] as const,
  mentionSuggestions: (query: string) => ["selection", "mentions", query] as const,
  quoteSnapshot: (symbols: string[]) =>
    ["selection", "quotes", [...symbols].sort().join(",")] as const,
  ideDispatchResult: (dispatchId: string) => ["selection", "dispatch", dispatchId] as const,

  // ── W9: wisdom graph, merge, share grants, profiles, notifications ──────────
  wisdomSearch: (q: string, filters?: WisdomFilters) =>
    ["wisdom", "search", q, filters?.certainty ?? "", filters?.layer ?? ""] as const,
  wisdomNode: (kind: string, id: string) => ["wisdom", "node", kind, id] as const,
  wisdomNeighbors: (kind: string, id: string, depth: number, rels: string) =>
    ["wisdom", "neighbors", kind, id, depth, rels] as const,
  wisdomProvenance: (kind: string, id: string) => ["wisdom", "provenance", kind, id] as const,
  wisdomOverview: ["wisdom", "overview"] as const,
  wisdomExperts: (q: string) => ["wisdom", "experts", q] as const,
  // Under the documents root on purpose: a merge commit changes the document, so both must
  // invalidate together.
  divergence: (id: string) => ["documents", "divergence", id] as const,
  revisionParts: (revisionId: string) => ["documents", "revisions", "parts", revisionId] as const,
  mergeSession: (sessionId: string) => ["documents", "merge", sessionId] as const,
  grants: (documentId: string) => ["documents", "grants", documentId] as const,
  // Public: never mixed with the authenticated roots, so a sign-out purge cannot orphan them.
  grantRead: (urlId: string) => ["public", "grant", urlId] as const,
  grantComments: (urlId: string) => ["public", "grant", urlId, "comments"] as const,
  profileOwn: ["profile", "own"] as const,
  cvInterview: ["profile", "interview"] as const,
  profileSearch: (q: ProfileSearchQuery) =>
    [
      "profile",
      "search",
      q.skills?.join(",") ?? "",
      q.availability ?? "",
      q.timezone ?? "",
      q.offset ?? 0,
    ] as const,
  notifications: (archived: boolean | undefined, offset: number) =>
    ["notifications", archived ?? "all", offset] as const,
  notificationsUnread: ["notifications", "unread"] as const,
  notificationPreferences: ["notifications", "preferences"] as const,
};

/** Everything document-shaped invalidates together — statuses are filters, not resources. */
export const DOCUMENT_ROOT = ["documents"] as const;

/** Flows, runs and schedules move together: running a flow changes all three surfaces. */
const AUTOMATION_ROOT = ["automation"] as const;
const RUN_ROOT = ["automation", "runs"] as const;
const FLOW_ROOT = ["automation", "flows"] as const;

/**
 * Thinkers move together: adding a persona to the workspace changes the catalog AND the
 * available set the picker reads, and binding one changes what a run would actually execute.
 * One root, so no surface can be left showing the state before the write.
 */
const THINKER_ROOT = ["thinkers"] as const;
const CAPABILITY_ROOT = ["capabilities"] as const;
const AI_ACTION_ROOT = ["ai-actions"] as const;

/** Health settings and the delivery ledger move together with nothing else in the app. */
const HEALTH_ROOT = ["health"] as const;
const DOCUMENT_TASK_ROOT = ["documentTasks"] as const;
const NUDGE_ROOT = ["nudges"] as const;

/** A project write moves collections in the sidebar too — the two roots invalidate together. */
const PROJECT_ROOT = ["projects"] as const;
/** W8 — every conversation write invalidates one root; both faces live under it. */
const CONVERSATION_ROOT = ["conversations"] as const;
const COMMENT_ROOT = ["comments"] as const;
const DOCUMENT_CHAT_ROOT = ["document-chat"] as const;
const AUTH_ROOT = ["governance", "auth"] as const;
const ORGANIZATION_ROOT = ["governance", "organizations"] as const;
const SOURCE_APPROVAL_ROOT = ["governance", "source-approvals"] as const;
const WEBHOOK_ROOT = ["governance", "webhooks"] as const;
const GIT_ROOT = ["git"] as const;

/** A run in one of these states can still change on its own, so the view has to keep asking. */
const LIVE_RUN_STATUSES = new Set(["requested", "running", "observing", "paused"]);

export function isRunLive(run: Pick<Run, "status">): boolean {
  return LIVE_RUN_STATUSES.has(run.status);
}

export function useSession() {
  const api = useApi();
  return useQuery<Session | null>({
    queryKey: queryKeys.session,
    queryFn: () => api.auth.session(),
    staleTime: 30_000,
  });
}

export function useViewer() {
  const api = useApi();
  return useQuery<Viewer>({
    queryKey: queryKeys.viewer,
    queryFn: () => api.viewer(),
    staleTime: 60_000,
  });
}

export function useWorkspaces() {
  const api = useApi();
  return useQuery<Workspace[]>({
    queryKey: queryKeys.workspaces,
    queryFn: () => api.workspaces.list(),
    staleTime: 60_000,
  });
}

export function useCurrentWorkspace() {
  const api = useApi();
  return useQuery<Workspace>({
    queryKey: queryKeys.currentWorkspace,
    queryFn: () => api.workspaces.current(),
    staleTime: 60_000,
  });
}

export function useCollections() {
  const api = useApi();
  return useQuery<Collection[]>({
    queryKey: queryKeys.collections,
    queryFn: () => api.collections.list(),
    staleTime: 60_000,
  });
}

/**
 * W10 §8 — the live list PLUS the archived ones. The platform has no archived-only endpoint:
 * asking for archived returns both, so the caller separates on `archivedAt`.
 */
export function useCollectionsWithArchived() {
  const api = useApi();
  return useQuery<Collection[]>({
    queryKey: queryKeys.collectionsWithArchived,
    queryFn: () => api.collections.list({ includeArchived: true }),
    staleTime: 60_000,
  });
}

/** Every collection write invalidates BOTH lists — archiving moves a row between them. */
function useCollectionMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
    },
    onError,
  });
}

export function useArchiveCollection() {
  const api = useApi();
  return useCollectionMutation((id: string) => api.collections.archive(id));
}

export function useRestoreCollection() {
  const api = useApi();
  return useCollectionMutation((id: string) => api.collections.restore(id));
}

export function useCollectionPeople(collectionId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.collectionPeople(collectionId),
    queryFn: () => api.collections.people.list(collectionId),
    enabled: Boolean(collectionId),
  });
}

export function useCollectionGroups(collectionId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.collectionGroups(collectionId),
    queryFn: () => api.collections.people.groups(collectionId),
    enabled: Boolean(collectionId),
  });
}

export function useGrantCollectionAccess() {
  const api = useApi();
  return useCollectionMutation(
    (input: { collectionId: string; userId: string; permission: CollectionPermission }) =>
      api.collections.people.add(input),
  );
}

export function useRevokeCollectionAccess() {
  const api = useApi();
  return useCollectionMutation((input: { collectionId: string; userId: string }) =>
    api.collections.people.remove(input),
  );
}

/** `add_group` returns `{success}` and nothing else — the invalidation IS the update. */
export function useGrantCollectionGroupAccess() {
  const api = useApi();
  return useCollectionMutation(
    (input: { collectionId: string; groupId: string; permission: CollectionPermission }) =>
      api.collections.people.addGroup(input),
  );
}

export function useRevokeCollectionGroupAccess() {
  const api = useApi();
  return useCollectionMutation((input: { collectionId: string; groupId: string }) =>
    api.collections.people.removeGroup(input),
  );
}

export function useCollection(id: string) {
  const api = useApi();
  return useQuery<Collection>({
    queryKey: queryKeys.collection(id),
    queryFn: () => api.collections.get(id),
    enabled: Boolean(id),
    // A collection that is gone stays gone: asking twice only doubles the console 404. Every
    // other failure keeps the shell's one retry.
    retry: (failureCount, error) => !(error instanceof NotFoundError) && failureCount < 1,
  });
}

/**
 * The collection a document *claims* to be in — asked for only when the viewer can actually
 * reach it. This estate carries 809 live documents whose collection was soft-deleted months
 * ago — 806 of them published AND unarchived, plus 2 archived and 1 draft (both figures are
 * right: 806 is that conjunction, which is why no single-column count reproduces it);
 * `collections.info` answers 404 for every one of them, on every
 * mount, which is console noise rather than information. `collections.list` is already loaded
 * for the sidebar, so "can I reach it" is a cache read, not a second request. While the list is
 * still loading nothing is asked; once it has loaded and does not contain the id, nothing is
 * ever asked. `undefined` therefore means "no collection to show", which is what the document
 * header already renders.
 */
export function useAccessibleCollection(id: string) {
  const api = useApi();
  const accessible = useCollections();
  const reachable = accessible.isSuccess
    ? accessible.data.some((collection) => collection.id === id)
    : undefined;

  return useQuery<Collection>({
    queryKey: queryKeys.collection(id),
    queryFn: () => api.collections.get(id),
    enabled: Boolean(id) && reachable === true,
    retry: (failureCount, error) => !(error instanceof NotFoundError) && failureCount < 1,
  });
}

export function useDocuments(query?: DocumentQuery) {
  const api = useApi();
  return useQuery<Document[]>({
    queryKey: queryKeys.documents(query),
    queryFn: () => api.documents.list(query),
  });
}

export function useDocument(id: string) {
  const api = useApi();
  return useQuery<Document>({
    queryKey: queryKeys.document(id),
    queryFn: () => api.documents.get(id),
    enabled: Boolean(id),
  });
}

export function useRevisions(id: string) {
  const api = useApi();
  return useQuery<Revision[]>({
    queryKey: queryKeys.revisions(id),
    queryFn: () => api.documents.revisions(id),
    enabled: Boolean(id),
  });
}

export function useSearch(query: string) {
  const api = useApi();
  return useQuery<Document[]>({
    queryKey: queryKeys.search(query),
    queryFn: () => api.search(query),
    enabled: query.trim().length > 0,
  });
}

/**
 * W10 §6 — the /search scene's query.
 *
 * `enabled` on a non-blank term because the endpoint short-circuits a blank one to an empty
 * result BEFORE applying any filter: firing it anyway would spend a request to be told nothing,
 * and — worse — would write a `SearchQuery` row for an empty search on every keystroke.
 */
export function useSearchResults(params: SearchParams) {
  const api = useApi();
  return useQuery<SearchResults>({
    queryKey: queryKeys.searchResults(params),
    queryFn: () => api.searches.run(params),
    enabled: params.query.trim().length > 0,
    placeholderData: (previous) => previous,
  });
}

/** The viewer's own recent searches — a by-product of searching, not a saved list. */
export function useRecentSearches(limit = 8) {
  const api = useApi();
  return useQuery<RecentSearch[]>({
    queryKey: queryKeys.recentSearches,
    queryFn: () => api.searches.recent({ limit }),
    staleTime: 60_000,
  });
}

export function useForgetSearch() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: { id?: string; query?: string }) => api.searches.forget(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recentSearches });
    },
    onError,
  });
}

export function useMembers() {
  const api = useApi();
  return useQuery<Member[]>({ queryKey: queryKeys.members, queryFn: () => api.members.list() });
}

export function useGroups() {
  const api = useApi();
  return useQuery<Group[]>({ queryKey: queryKeys.groups, queryFn: () => api.groups.list() });
}

export function useApiKeys() {
  const api = useApi();
  return useQuery<ApiKey[]>({ queryKey: queryKeys.apiKeys, queryFn: () => api.apiKeys.list() });
}

/**
 * W10 §2 — the viewer's stars, list + lookup + toggle.
 *
 * A star is keyed by its OWN id, so "unstar this document" needs the star row — which is why
 * the list is the source of truth for both questions the UI asks ("is it starred?" and "which
 * row do I delete?"). `staleTime` is short rather than zero: the sidebar and every star button
 * read the same entry, and a stale answer here means a duplicate star.
 */
export function useStars() {
  const api = useApi();
  return useQuery<StarList>({
    queryKey: queryKeys.stars,
    queryFn: () => api.stars.list(),
    staleTime: 30_000,
  });
}

/**
 * `entityId → star id`. A document id and a collection id can never collide (both are GUIDs),
 * so one map answers for both — which is what lets ONE star action serve documents and
 * collections alike.
 */
export function useStarLookup(): (entityId: string) => string | undefined {
  const { data } = useStars();
  return (entityId: string) =>
    data?.stars.find((star) => star.documentId === entityId || star.collectionId === entityId)?.id;
}

/**
 * The star slice the ACTION CONTEXT gets: the same two verbs, but the lookup reads the cache
 * WITHOUT subscribing to it.
 *
 * `useActionContext` is assembled by every document row, every menu and the command bar, so a
 * `useQuery` there would put one subscription per row on a key nothing on screen is rendering
 * — measurable render cost for a question that is only ever asked at the moment a menu opens,
 * by which time the sidebar's Starred section has already filled the cache. Components that
 * RENDER star state (`StarButton`, `StarredTree`) still subscribe, through `useStars`.
 *
 * When the cache is cold every entity reads as "not starred", which is why the toggle below
 * resolves the real list before it writes: a wrong label is recoverable, a duplicate row is not.
 */
export function useStarActions(): {
  idFor: (entityId: string) => string | undefined;
  toggle: (target: StarTarget) => void;
} {
  const queryClient = useQueryClient();
  const toggle = useToggleStar();
  return {
    idFor: (entityId: string) =>
      queryClient
        .getQueryData<StarList>(queryKeys.stars)
        ?.stars.find((star) => star.documentId === entityId || star.collectionId === entityId)?.id,
    toggle: (target) => toggle.mutate(target),
  };
}

/**
 * Star or unstar, deciding which from the CURRENT list rather than from a render-time snapshot.
 *
 * `StarsController.Create` inserts unconditionally — no dedupe — so a toggle that trusted a
 * stale cache would quietly create a second star and show the document twice in the sidebar.
 * `ensureQueryData` resolves the list first (cached when fresh, fetched when not), which makes
 * the double-click case a delete rather than a duplicate.
 */
export function useToggleStar() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();

  return useMutation({
    mutationFn: async (target: StarTarget) => {
      const entityId = target.documentId ?? target.collectionId;
      const list = await queryClient.ensureQueryData<StarList>({
        queryKey: queryKeys.stars,
        queryFn: () => api.stars.list(),
      });
      const existing = list.stars.find(
        (star) => star.documentId === entityId || star.collectionId === entityId,
      );
      if (existing) {
        await api.stars.delete(existing.id);
        return { starred: false } as const;
      }
      await api.stars.create(target);
      return { starred: true } as const;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stars });
    },
    onError,
  });
}

/**
 * W10 §3 — the workspace's pins for one collection (or, with no argument, the home pins).
 */
export function usePins(collectionId?: string) {
  const api = useApi();
  return useQuery<PinList>({
    queryKey: queryKeys.pins(collectionId),
    queryFn: () => api.pins.list(collectionId ? { collectionId } : {}),
    staleTime: 30_000,
  });
}

/**
 * Pin or unpin a document within one collection, deciding from the CURRENT list.
 *
 * Same reasoning as `useToggleStar`: `PinsController.Create` inserts unconditionally, so a
 * toggle that trusted a render-time snapshot would pin the same document twice and show it
 * twice at the top of the collection.
 */
export function useTogglePin(collectionId?: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();

  return useMutation({
    mutationFn: async (documentId: string) => {
      const list = await queryClient.ensureQueryData<PinList>({
        queryKey: queryKeys.pins(collectionId),
        queryFn: () => api.pins.list(collectionId ? { collectionId } : {}),
      });
      const existing = list.pins.find((pin) => pin.documentId === documentId);
      if (existing) {
        await api.pins.delete(existing.id);
        return { pinned: false } as const;
      }
      await api.pins.create({ documentId, ...(collectionId ? { collectionId } : {}) });
      return { pinned: true } as const;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.pins(collectionId) });
    },
    onError,
  });
}

/**
 * W10 §4 — the share link on one document, or null when it has never been shared.
 *
 * Null is a RESULT, not an error: `shares.info` answers 204 for that state. `retry: false`
 * because a refused share read (403 on a document the viewer may not share) is an answer too,
 * and retrying it three times only delays the dialog.
 */
export function useDocumentShare(documentId: string) {
  const api = useApi();
  return useQuery<ShareLink | null>({
    queryKey: queryKeys.documentShare(documentId),
    queryFn: () => api.shareLinks.forDocument(documentId),
    enabled: Boolean(documentId),
    retry: false,
  });
}

/** Both writes invalidate the whole `shares` root — the settings list shows the same rows. */
function useShareMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shares });
    },
    onError,
  });
}

/**
 * Create-or-update. `shares.create` is an UPSERT server-side, so this is also the right call
 * for "turn the web switch on for a document that already has a link" — but the dialog sends
 * `update` when it holds an id, which keeps the intent legible on the wire.
 */
export function useCreateShareLink() {
  const api = useApi();
  return useShareMutation((input: { documentId: string } & ShareLinkPatch) =>
    api.shareLinks.create(input),
  );
}

export function useUpdateShareLink() {
  const api = useApi();
  return useShareMutation((input: { id: string } & ShareLinkPatch) => api.shareLinks.update(input));
}

export function useRevokeShareLink() {
  const api = useApi();
  return useShareMutation((id: string) => api.shareLinks.revoke(id));
}

/** Poll cadence and ceiling for an export job. */
const EXPORT_POLL_MS = 1200;
const EXPORT_TIMEOUT_MS = 3 * 60_000;

/**
 * W10 §5 — export a document and hand the file to the browser.
 *
 * A plain function, not a hook, because BOTH callers need it and only one of them is a
 * component: the ⌘K/menu action receives an `ApiClient` and a `t`, and nothing else. Three
 * things happen here that cannot happen anywhere else:
 *
 * 1. **The poll.** `documents.export` enqueues a Hangfire job and the row comes back `pending`.
 *    This waits for `complete`, with a ceiling — an export that never finishes must end as a
 *    message, not a spinner that outlives the page.
 * 2. **The bearer.** The artefact is fetched through the adapter, because an `<a href>` or a
 *    `window.open` on `fileOperations.redirect` arrives ANONYMOUS under a PKCE session.
 * 3. **The save.** The bytes become an object URL and a synthesised click; the URL is revoked
 *    afterwards, or every export leaks a blob for the lifetime of the tab.
 *
 * The artefact is a zip whatever the format was — the server names its storage key `.zip`
 * before it even parses the format — so that is what the file is called.
 */
export async function exportDocumentFile(
  api: ApiClient,
  input: { documentId: string; format: ExportFormat; title: string },
  t: (key: string) => string,
): Promise<void> {
  const started = await api.exports.start({ documentId: input.documentId, format: input.format });

  let operation = started;
  const deadline = Date.now() + EXPORT_TIMEOUT_MS;
  while (operation.state !== "complete") {
    if (operation.state === "failed") {
      throw new Error(operation.error || t("The export failed"));
    }
    if (Date.now() > deadline) {
      throw new Error(t("The export is taking too long — it will appear in Export settings"));
    }
    await new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_MS));
    operation = await api.exports.status(operation.id);
  }

  saveBlob(await api.exports.download(operation.id), `${input.title || "document"}.zip`);
}

export function useExportDocument() {
  const api = useApi();
  const { t } = useTranslation();
  const onError = useErrorToast();

  return useMutation({
    mutationFn: (input: { documentId: string; format: ExportFormat; title: string }) =>
      exportDocumentFile(api, input, (key) => t(key)),
    onError,
  });
}

/**
 * Hand a blob to the browser as a download. Guarded, because a synthesised anchor click is a
 * DOM side effect: in a test environment (or a locked-down webview) `createObjectURL` may not
 * exist, and a failed save must not turn into an unhandled rejection on the caller.
 */
function saveBlob(blob: Blob, filename: string): void {
  if (typeof URL?.createObjectURL !== "function" || typeof document === "undefined") {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** W10 §4 — `documents.unpublish`. Publish stays on `usePublishDocument` above. */
export function useUnpublishDocument() {
  const api = useApi();
  return useDocumentMutation((id: string) => api.documents.unpublish(id));
}

export function useShares() {
  const api = useApi();
  return useQuery<Share[]>({ queryKey: queryKeys.shares, queryFn: () => api.shares.list() });
}

export function usePublicShare(shareId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.publicShare(shareId),
    queryFn: () => api.public.share(shareId),
    enabled: Boolean(shareId),
    retry: false,
  });
}

export function usePublicProfile(slug: string) {
  const api = useApi();
  return useQuery<PublicProfileCv>({
    queryKey: queryKeys.publicProfile(slug),
    queryFn: () => api.public.profile(slug),
    enabled: Boolean(slug),
    retry: false,
  });
}

/** Surfaces the server's message verbatim — the reason a mutation failed is server truth. */
function useErrorToast() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const message = error instanceof Error ? error.message : t("Something went wrong");
    toast.error(message);
  };
}

function useDocumentMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  onDone?: (result: TResult, args: TArgs) => void,
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();

  return useMutation({
    mutationFn,
    onSuccess: (result, args) => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENT_ROOT });
      onDone?.(result, args);
    },
    onError,
  });
}

/**
 * Entering another workspace. The adapter re-scopes the session and reloads, so there is no
 * success path to render here — only a failure worth showing (`not_a_member`, suspended, …).
 */
export function useSwitchWorkspace() {
  const api = useApi();
  const onError = useErrorToast();

  return useMutation({
    mutationFn: ({
      workspaceId,
      rememberAutoEnter,
      returnTo,
    }: {
      workspaceId: string;
      rememberAutoEnter?: boolean;
      returnTo?: string;
    }) =>
      api.workspaces.switch(workspaceId, {
        ...(rememberAutoEnter === undefined ? {} : { rememberAutoEnter }),
        ...(returnTo === undefined ? {} : { returnTo }),
      }),
    onError,
  });
}

/**
 * W10 §9 — the workspace's templates. A separate query rather than a filter on `useDocuments`,
 * because `documents.list` needs `template: true` to see them AT ALL: the two lists are
 * disjoint on the wire, not a subset relation.
 */
export function useTemplates() {
  const api = useApi();
  return useQuery<Document[]>({
    queryKey: queryKeys.templates,
    queryFn: () => api.templates.list(),
    staleTime: 60_000,
  });
}

/** A COPY becomes the template; the source document is untouched. */
export function useCreateTemplateFrom() {
  const api = useApi();
  return useDocumentMutation(
    (input: { documentId: string; title: string; text: string; collectionId?: string | null }) =>
      api.templates.createFrom(input),
  );
}

/** ⚠ Irreversible: `documents.templatize` turns the document itself into a template. */
export function usePromoteToTemplate() {
  const api = useApi();
  return useDocumentMutation((input: { documentId: string; collectionId?: string }) =>
    api.templates.promote(input),
  );
}

/** New document seeded from a template — one call; the server copies the body. */
export function useCreateDocumentFromTemplate() {
  const api = useApi();
  return useDocumentMutation(
    (input: { templateId: string; title?: string; collectionId?: string | null }) =>
      api.templates.createDocument(input),
  );
}

export function useCreateDocument() {
  const api = useApi();
  return useDocumentMutation(
    (data: { title: string; collectionId?: string | null; text?: string; publish?: boolean }) =>
      api.documents.create(data),
  );
}

export function useUpdateDocument() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useDocumentMutation(
    ({ id, data }: { id: string; data: DocumentPatch }) => api.documents.update(id, data),
    (document) => queryClient.setQueryData(queryKeys.document(document.id), document),
  );
}

/**
 * W10 §1 — move. Invalidates the whole document root (the source list, the destination list and
 * the sidebar tree all change) and writes the moved row straight into its detail cache, so the
 * open document's breadcrumb updates without a second round trip.
 */
export function useMoveDocument() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useDocumentMutation(
    (input: { id: string; collectionId?: string; parentDocumentId?: string }) =>
      api.documents.move(input),
    (document) => {
      queryClient.setQueryData(queryKeys.document(document.id), document);
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
    },
  );
}

export function usePublishDocument() {
  const api = useApi();
  return useDocumentMutation((id: string) => api.documents.publish(id));
}

export function useArchiveDocument() {
  const api = useApi();
  return useDocumentMutation((id: string) => api.documents.archive(id));
}

export function useRestoreDocument() {
  const api = useApi();
  return useDocumentMutation((id: string) => api.documents.restore(id));
}

export function useDeleteDocument() {
  const api = useApi();
  return useDocumentMutation((id: string) => api.documents.delete(id));
}

export function usePermanentlyDeleteDocument() {
  const api = useApi();
  return useDocumentMutation((id: string) => api.documents.permanentlyDelete(id));
}

// ── Document Sculptor ────────────────────────────────────────────────────────

/**
 * The newest sculpt on a document, folded together with whether sculpting is available at
 * all — a 403 `sculpt_disabled` is a state ("hide every affordance"), not an error to toast.
 */
export interface SculptState {
  disabled: boolean;
  latest: SculptLatest | null;
}

export function useSculptLatest(documentId: string, enabled = true) {
  const api = useApi();
  return useQuery<SculptState>({
    queryKey: queryKeys.sculptLatest(documentId),
    queryFn: async () => {
      try {
        return { disabled: false, latest: await api.documents.sculptLatest(documentId) };
      } catch (error) {
        if (error instanceof SculptDisabledError) {
          return { disabled: true, latest: null };
        }
        throw error;
      }
    },
    enabled: Boolean(documentId) && enabled,
    retry: false,
  });
}

/**
 * Run one sculpt. No error toast here on purpose: SculptConfirmRequiredError is control
 * flow (the dialog re-asks with the token), so the caller owns error presentation.
 */
export function useSculpt() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, request }: { documentId: string; request: SculptRunRequest }) =>
      api.documents.sculpt(documentId, request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: DOCUMENT_ROOT }),
  });
}

export function useResolveSculpt() {
  const api = useApi();
  return useDocumentMutation(
    ({
      documentId,
      opId,
      action,
    }: {
      documentId: string;
      opId: string;
      action: "accept" | "reject" | "discard";
    }) => api.documents.sculptResolve(documentId, opId, action),
  );
}

export function useRevertSculpt() {
  const api = useApi();
  return useDocumentMutation(({ documentId, opId }: { documentId: string; opId: string }) =>
    api.documents.sculptRevert(documentId, opId),
  );
}

/**
 * The editor's save path. Slate never writes a document body through `documents.update`:
 * whether a document's live state sits in a collaboration room is server knowledge, and a
 * REST write against a collab doc clobbers newer state. A sculpt with `providedMarkdown`
 * is the platform's deterministic write — the server picks the safe apply path, takes a
 * checkpoint (every save is revertible), and a destructive-confirm 409 is answered with
 * the token it carries. If the apply lane is unreachable (`apply_lane_unsupported`, a
 * v2-collab room), the save degrades honestly to a reviewable proposal.
 */
export function useSaveDocumentBody() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<SculptOutcome, unknown, { documentId: string; markdown: string }>({
    mutationFn: async ({ documentId, markdown }) => {
      const request: SculptRunRequest = {
        op: "compose",
        params: { providedMarkdown: markdown },
        requestedLane: "act",
        idempotencyKey: crypto.randomUUID(),
      };
      try {
        return await api.documents.sculpt(documentId, request);
      } catch (error) {
        if (error instanceof SculptConfirmRequiredError) {
          return api.documents.sculpt(documentId, { ...request, confirmToken: error.confirmToken });
        }
        if (error instanceof SculptError && error.code === "apply_lane_unsupported") {
          return api.documents.sculpt(documentId, { ...request, requestedLane: "propose" });
        }
        throw error;
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: DOCUMENT_ROOT }),
  });
}

export function useCreateCollection() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (data: { name: string }) => api.collections.create(data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.collections }),
    onError,
  });
}

export function useUpdateCollection() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CollectionPatch }) =>
      api.collections.update(id, data),
    onSuccess: (collection) => {
      queryClient.setQueryData(queryKeys.collection(collection.id), collection);
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
    },
    onError,
  });
}

export function useDeleteCollection() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: string) => api.collections.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
      void queryClient.invalidateQueries({ queryKey: DOCUMENT_ROOT });
    },
    onError,
  });
}

export function useCreateApiKey() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (data: { name: string }) => api.apiKeys.create(data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys }),
    onError,
  });
}

export function useRevokeApiKey() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: string) => api.apiKeys.revoke(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys }),
    onError,
  });
}

export function useRevokeShare() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: string) => api.shares.revoke(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.shares }),
    onError,
  });
}

// ── Automation ───────────────────────────────────────────────────────────────

export function useAgents() {
  const api = useApi();
  return useQuery<Agent[]>({
    queryKey: queryKeys.agents,
    queryFn: () => api.automation.agents.list(),
    staleTime: 60_000,
  });
}

export function useFlows(includeArchived = false) {
  const api = useApi();
  return useQuery<FlowList>({
    queryKey: queryKeys.flows(includeArchived),
    queryFn: () => api.automation.flows.list({ includeArchived }),
  });
}

/**
 * R4 — the flows linked to one entity. Its own key shape (kind + id), NOT the `flows` key: two
 * panels sharing one cache entry would each serve the other's rows.
 */
export function useEntityFlows(kind: "collection" | "document" | "project", id: string) {
  const api = useApi();
  return useQuery<FlowList>({
    queryKey: queryKeys.entityFlows(kind, id),
    queryFn: () => api.automation.flows.list({ entity: { kind, id } }),
    enabled: id.length > 0,
  });
}

export function useFlow(slug: string) {
  const api = useApi();
  return useQuery<FlowDetail>({
    queryKey: queryKeys.flow(slug),
    queryFn: () => api.automation.flows.get(slug),
    enabled: Boolean(slug),
  });
}

export function useFlowVersions(slug: string) {
  const api = useApi();
  return useQuery<FlowVersion[]>({
    queryKey: queryKeys.flowVersions(slug),
    queryFn: () => api.automation.flows.versions(slug),
    enabled: Boolean(slug),
  });
}

/**
 * The studio's load: the record and its parsed definition together (W3 §E.4). A `version`
 * reads that historical one — which is also the first half of Restore, there being no rollback
 * endpoint: read version N, then save it as a new head.
 */
export function useFlowDefinition(
  slug: string,
  version?: number,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery<FlowDefinitionResult>({
    queryKey: queryKeys.flowDefinition(slug, version),
    queryFn: () => api.automation.flows.definition(slug, version),
    // `enabled` is a caller's switch, not a replacement for the slug check: Restore reads a
    // historical version only once a version has been picked, and must not fetch before that.
    enabled: Boolean(slug) && options.enabled !== false,
  });
}

/**
 * Saving invalidates the flow ROOT (the list's active count and version count both move) and,
 * explicitly, this flow's definition key — the studio swaps its draft for the server echo, and
 * a stale cached definition is how an author's next edit gets built on the previous version.
 */
export function useSaveFlow() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useAutomationMutation(
    (input: { slug: string; definition: unknown; note?: string }) =>
      api.automation.flows.save(input),
    FLOW_ROOT,
    (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.flow(input.slug) });
      void queryClient.invalidateQueries({
        queryKey: ["automation", "flows", "definition", input.slug],
      });
    },
  );
}

export function useAttachFlowDoc() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useAutomationMutation(
    (input: { slug: string; documentId?: string; collectionId?: string }) =>
      api.automation.flows.attachDoc(input),
    FLOW_ROOT,
    (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.flow(input.slug) });
    },
  );
}

/**
 * The last few runs of one node. Kept out of the flow root on purpose: it is evidence about
 * the PAST, so a save must not invalidate it, and it is only read when the Node tab's history
 * panel is open — hence `enabled` on both ids.
 */
export function useFlowNodeHistory(slug: string, nodeId: string, limit = 3) {
  const api = useApi();
  return useQuery<FlowNodeHistory>({
    queryKey: queryKeys.flowNodeHistory(slug, nodeId, limit),
    queryFn: () => api.automation.flows.nodeHistory({ slug, nodeId, limit }),
    enabled: Boolean(slug && nodeId),
    staleTime: 60_000,
  });
}

function useAutomationMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  root: readonly string[],
  onDone?: (result: TResult, args: TArgs) => void,
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();

  return useMutation({
    mutationFn,
    onSuccess: (result, args) => {
      void queryClient.invalidateQueries({ queryKey: root });
      onDone?.(result, args);
    },
    onError,
  });
}

export function useSetFlowStatus() {
  const api = useApi();
  return useAutomationMutation(
    ({ slug, status }: { slug: string; status: FlowStatus }) =>
      api.automation.flows.setStatus(slug, status),
    FLOW_ROOT,
  );
}

/**
 * Starting a flow. There is no result to wait for — the server answers with where the work
 * landed, and the run (if there is one) shows up in the ledger.
 */
export function useRunFlow() {
  const api = useApi();
  return useAutomationMutation(
    ({ slug, request }: { slug: string; request?: FlowRunRequest }) =>
      api.automation.flows.run(slug, request),
    AUTOMATION_ROOT,
  );
}

export function useDeleteFlow() {
  const api = useApi();
  return useAutomationMutation((slug: string) => api.automation.flows.delete(slug), FLOW_ROOT);
}

export function useRuns(query?: RunQuery) {
  const api = useApi();
  return useQuery<Run[]>({
    queryKey: queryKeys.runs(query),
    queryFn: () => api.automation.runs.list(query),
    // A live run changes without anybody touching the page; a settled list does not.
    refetchInterval: (result) => (result.state.data?.some(isRunLive) ? 10_000 : false),
  });
}

export function useRun(id: string) {
  const api = useApi();
  return useQuery<Run>({
    queryKey: queryKeys.run(id),
    queryFn: () => api.automation.runs.get(id),
    enabled: Boolean(id),
    refetchInterval: (result) =>
      result.state.data && isRunLive(result.state.data) ? 5_000 : false,
  });
}

export function useRunSteps(id: string, live = false) {
  const api = useApi();
  return useQuery<RunStep[]>({
    queryKey: queryKeys.runSteps(id),
    queryFn: () => api.automation.runs.steps(id),
    enabled: Boolean(id),
    refetchInterval: live ? 10_000 : false,
  });
}

/**
 * Weave (Docit Work 15 §7.2) — what each node of a run IS: executor kind + the tile it carries.
 * Polls like the other run reads while the run is live, so a tile a person moves on the board
 * shows up on the canvas within one tick.
 */
export function useRunTree(id: string, live = false) {
  const api = useApi();
  return useQuery<RunTree>({
    queryKey: queryKeys.runTree(id),
    queryFn: () => api.automation.runs.tree(id),
    enabled: Boolean(id),
    refetchInterval: live ? 10_000 : false,
    retry: false,
  });
}

export function useRunLogs(id: string, live = false) {
  const api = useApi();
  return useQuery<RunLogLine[]>({
    queryKey: queryKeys.runLogs(id),
    queryFn: () => api.automation.runs.logs(id),
    enabled: Boolean(id),
    refetchInterval: live ? 10_000 : false,
  });
}

/** Fed only by `runs.stats`, independent of the run list — never aggregated client-side. */
export function useRunStats() {
  const api = useApi();
  return useQuery<RunStats>({
    queryKey: queryKeys.runStats,
    queryFn: () => api.automation.runs.stats(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useControlRun() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, action }: { id: string; action: RunControl }) => api.automation.runs.control(id, action),
    RUN_ROOT,
  );
}

export function useApproveRun() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, decision }: { id: string; decision: RunApproval }) =>
      api.automation.runs.approve(id, decision),
    RUN_ROOT,
  );
}

export function useScheduledTasks() {
  const api = useApi();
  return useQuery<ScheduledTask[]>({
    queryKey: queryKeys.tasks,
    queryFn: () => api.automation.tasks.list(),
  });
}

export function useCreateScheduledTask() {
  const api = useApi();
  return useAutomationMutation(
    (draft: ScheduledTaskDraft) => api.automation.tasks.create(draft),
    AUTOMATION_ROOT,
  );
}

export function useSetTaskPaused() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, paused }: { id: string; paused: boolean }) => api.automation.tasks.setPaused(id, paused),
    AUTOMATION_ROOT,
  );
}

export function useRunTaskNow() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.automation.tasks.runNow(id), AUTOMATION_ROOT);
}

export function useDeleteScheduledTask() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.automation.tasks.delete(id), AUTOMATION_ROOT);
}

export function useDispatches(activeOnly = false) {
  const api = useApi();
  return useQuery<Dispatch[]>({
    queryKey: queryKeys.dispatches(activeOnly),
    queryFn: () => api.automation.dispatches.list({ activeOnly }),
    refetchInterval: 15_000,
  });
}

// ── The AI ledger ────────────────────────────────────────────────────────────

export function useAiUsage(query?: AiUsageQuery) {
  const api = useApi();
  return useQuery<AiUsage>({
    queryKey: queryKeys.aiUsage(query?.days ?? 0),
    queryFn: () => api.ai.usage(query),
    // A roll-up over a whole window drifts slowly; a minute of staleness is honest here.
    staleTime: 60_000,
  });
}

export function useAiCalls(query?: AiCallsQuery) {
  const api = useApi();
  return useQuery<AiCallsPage>({
    queryKey: queryKeys.aiCalls(query),
    queryFn: () => api.ai.calls(query),
    staleTime: 30_000,
  });
}

export function useAiQueueDigest() {
  const api = useApi();
  return useQuery<QueueDigest>({
    queryKey: queryKeys.aiQueueDigest,
    // Queue depth moves on its own while the page sits open, so the view keeps asking.
    queryFn: () => api.ai.queueDigest(),
    refetchInterval: 30_000,
  });
}

export function useActivity(query?: ActivityQuery) {
  const api = useApi();
  return useQuery<ActivityPage>({
    queryKey: queryKeys.activity(query),
    queryFn: () => api.automation.activity.list(query),
    // Paging replaces the page rather than blanking it — the list must not flicker back to empty.
    placeholderData: (previous) => previous,
  });
}

export function useLogin() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();

  return useMutation({
    mutationFn: (email: string) => api.auth.login(email),
    onSuccess: (session) => {
      queryClient.setQueryData(queryKeys.session, session);
      void queryClient.invalidateQueries();
    },
    onError,
  });
}

export function useLogout() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.session, null);
      queryClient.clear();
    },
  });
}

// ── AI administration (W1) ───────────────────────────────────────────────────
// Thinkers, AI permissions and AI actions. Every mutation invalidates its whole root rather
// than patching a single entry: these surfaces are cross-referential (a binding changes the
// catalog row and the available set and what a run would execute), and a half-refreshed screen
// is how a settings page starts lying about what is configured.

/** One root for everything thinker-shaped, so no surface is left showing the pre-write state. */
function useAiAdminMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  root: readonly string[],
  onDone?: (result: TResult, args: TArgs) => void,
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();

  return useMutation({
    mutationFn,
    onSuccess: (result, args) => {
      void queryClient.invalidateQueries({ queryKey: root });
      onDone?.(result, args);
    },
    onError,
  });
}

/**
 * The workspace's selectable set — what it can actually hand work to. This is the picker's
 * source, and the same list the server would resolve at send-time.
 */
export function useAvailableThinkers() {
  const api = useApi();
  return useQuery<AvailableThinker[]>({
    queryKey: queryKeys.thinkers,
    queryFn: () => api.thinkers.available(),
    staleTime: 60_000,
  });
}

/** Human assistants whose area tags appear in a document. Skipped when there is no document. */
export function useSuggestedThinkers(documentId: string | null | undefined) {
  const api = useApi();
  return useQuery<AvailableThinker[]>({
    queryKey: queryKeys.suggestedThinkers(documentId ?? ""),
    queryFn: () => api.thinkers.suggested(documentId ?? ""),
    enabled: Boolean(documentId),
    staleTime: 60_000,
  });
}

export function useThinkerCatalog() {
  const api = useApi();
  return useQuery<CatalogThinker[]>({
    queryKey: queryKeys.thinkerCatalog,
    queryFn: () => api.thinkers.catalog(),
  });
}

/** Catalog health, so an empty picker is never mistaken for an outage. */
export function useThinkerCatalogStatus() {
  const api = useApi();
  return useQuery<CatalogStatus>({
    queryKey: queryKeys.thinkerCatalogStatus,
    queryFn: () => api.thinkers.catalogStatus(),
    staleTime: 60_000,
  });
}

export function usePersona(id: string | null) {
  const api = useApi();
  return useQuery<CatalogThinker>({
    queryKey: queryKeys.persona(id ?? ""),
    queryFn: () => api.thinkers.persona(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useThinkerShares(id: string | null) {
  const api = useApi();
  return useQuery<ThinkerShare[]>({
    queryKey: queryKeys.thinkerShares(id ?? ""),
    queryFn: () => api.thinkers.shares(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useThinkerGroups() {
  const api = useApi();
  return useQuery<GroupOption[]>({
    queryKey: queryKeys.thinkerGroups,
    queryFn: () => api.thinkers.groups(),
    staleTime: 300_000,
  });
}

export function useLocalThinkers() {
  const api = useApi();
  return useQuery<LocalThinker[]>({
    queryKey: queryKeys.localThinkers,
    queryFn: () => api.thinkers.local.list(),
  });
}

/**
 * What is installed on an Ollama host. Never cached: the set changes the moment somebody runs
 * `ollama pull`, and a stale list is indistinguishable from a model that failed to install.
 */
export function useLocalThinkerModels(baseUrl: string, enabled = true) {
  const api = useApi();
  return useQuery<LocalThinkerModels>({
    queryKey: queryKeys.localThinkerModels(baseUrl),
    queryFn: () => api.thinkers.local.models(baseUrl),
    enabled: enabled && baseUrl.length > 0,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateThinker() {
  const api = useApi();
  return useAiAdminMutation((draft: ThinkerDraft) => api.thinkers.create(draft), THINKER_ROOT);
}

export function useUpdateThinker() {
  const api = useApi();
  return useAiAdminMutation(
    ({ id, draft }: { id: string; draft: ThinkerDraft }) => api.thinkers.update(id, draft),
    THINKER_ROOT,
  );
}

/** Retires the persona everywhere. The narrower verb is `useRemoveThinkerFromWorkspace`. */
export function useDeleteThinker() {
  const api = useApi();
  return useAiAdminMutation((id: string) => api.thinkers.delete(id), THINKER_ROOT);
}

export function useUpdatePersona() {
  const api = useApi();
  return useAiAdminMutation(
    ({ id, patch }: { id: string; patch: PersonaPatch }) => api.thinkers.updatePersona(id, patch),
    THINKER_ROOT,
  );
}

export function useImportPersonas() {
  const api = useApi();
  return useAiAdminMutation(
    (options: { includeBuiltIns?: boolean } | undefined) => api.thinkers.importPersonas(options),
    THINKER_ROOT,
  );
}

export function useAddThinkerToWorkspace() {
  const api = useApi();
  return useAiAdminMutation(
    (draft: WorkspaceThinkerDraft) => api.thinkers.addToWorkspace(draft),
    THINKER_ROOT,
  );
}

export function useRemoveThinkerFromWorkspace() {
  const api = useApi();
  return useAiAdminMutation((id: string) => api.thinkers.removeFromWorkspace(id), THINKER_ROOT);
}

export function useSetThinkerBinding() {
  const api = useApi();
  return useAiAdminMutation(
    ({ id, binding }: { id: string; binding: ThinkerBinding }) =>
      api.thinkers.setBinding(id, binding),
    THINKER_ROOT,
  );
}

export function useShareThinker() {
  const api = useApi();
  return useAiAdminMutation(
    ({ id, draft }: { id: string; draft: ThinkerShareDraft }) => api.thinkers.share(id, draft),
    THINKER_ROOT,
  );
}

export function useRevokeThinkerShare() {
  const api = useApi();
  return useAiAdminMutation((grantId: string) => api.thinkers.revokeShare(grantId), THINKER_ROOT);
}

export function useCreateLocalThinker() {
  const api = useApi();
  return useAiAdminMutation(
    (draft: LocalThinkerDraft) => api.thinkers.local.create(draft),
    THINKER_ROOT,
  );
}

export function useUpdateLocalThinker() {
  const api = useApi();
  return useAiAdminMutation(
    ({ id, draft }: { id: string; draft: LocalThinkerDraft }) =>
      api.thinkers.local.update(id, draft),
    THINKER_ROOT,
  );
}

export function useDeleteLocalThinker() {
  const api = useApi();
  return useAiAdminMutation((id: string) => api.thinkers.local.delete(id), THINKER_ROOT);
}

/**
 * Probe a host on demand. A mutation rather than a query because it is an act the user asked
 * for — and because the answer must never be served from cache.
 */
export function useTestLocalThinker() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (baseUrl: string) => api.thinkers.local.test(baseUrl),
    onError,
  });
}

// ── AI permissions ───────────────────────────────────────────────────────────

export function useCapabilityPolicies() {
  const api = useApi();
  return useQuery<CapabilityPolicy[]>({
    queryKey: queryKeys.capabilityPolicies,
    queryFn: () => api.capabilities.policies(),
  });
}

export function useCapabilityCatalog() {
  const api = useApi();
  return useQuery<CapabilityCatalog>({
    queryKey: queryKeys.capabilityCatalog,
    queryFn: () => api.capabilities.catalog(),
    staleTime: 300_000,
  });
}

export function useCreateCapabilityPolicy() {
  const api = useApi();
  return useAiAdminMutation(
    (draft: CapabilityPolicyDraft) => api.capabilities.create(draft),
    CAPABILITY_ROOT,
  );
}

export function useDeleteCapabilityPolicy() {
  const api = useApi();
  return useAiAdminMutation((id: string) => api.capabilities.delete(id), CAPABILITY_ROOT);
}

// ── AI actions ───────────────────────────────────────────────────────────────

export function useAiActionList() {
  const api = useApi();
  return useQuery<SelectionAction[]>({
    queryKey: queryKeys.aiActions,
    queryFn: () => api.aiActions.list(),
  });
}

/** The provider gate. Short-lived so a key set on the server surfaces without a hard reload. */
export function useAiProviderStatus() {
  const api = useApi();
  return useQuery<AiProviderStatus>({
    queryKey: queryKeys.aiProviderStatus,
    queryFn: () => api.aiActions.providerStatus(),
    staleTime: 60_000,
  });
}

export function useCreateAiAction() {
  const api = useApi();
  return useAiAdminMutation(
    (draft: SelectionActionDraft) => api.aiActions.create(draft),
    AI_ACTION_ROOT,
  );
}

export function useUpdateAiAction() {
  const api = useApi();
  return useAiAdminMutation(
    ({ id, draft }: { id: string; draft: SelectionActionDraft }) => api.aiActions.update(id, draft),
    AI_ACTION_ROOT,
  );
}

export function useDeleteAiAction() {
  const api = useApi();
  return useAiAdminMutation((id: string) => api.aiActions.delete(id), AI_ACTION_ROOT);
}

export function useReorderAiActions() {
  const api = useApi();
  return useAiAdminMutation(
    (items: Array<{ id: string; sortOrder: number }>) => api.aiActions.reorder(items),
    AI_ACTION_ROOT,
  );
}

// ── Automation operations (W2) ────────────────────────────────────────────────
// The machinery board, the command ledger, run control beyond the four verbs above, flow
// health/billing, the delivery ledger, document tasks and nudges. Every board/run mutation
// invalidates the whole "automation" root; ledger reads invalidate only their own key.

/** The Tasks front door's one snapshot — polled every 10s always, per the wave's live-update decision. */
export function useTaskBoard(activeOnly = false) {
  const api = useApi();
  return useQuery<TaskBoard>({
    queryKey: queryKeys.taskBoard(activeOnly),
    queryFn: () => api.automation.board.snapshot({ activeOnly }),
    refetchInterval: 10_000,
  });
}

export function useLedgerActions(runId?: string | null, failedOnly = false) {
  const api = useApi();
  return useQuery<LedgerPage>({
    queryKey: queryKeys.ledgerActions(runId ?? undefined, failedOnly),
    queryFn: () =>
      api.automation.ledger.actions({
        ...(runId !== undefined ? { runId: runId ?? undefined } : {}),
        ...(failedOnly ? { failedOnly: true } : {}),
      }),
  });
}

export function useLedgerReport(
  runId: string | undefined,
  groupBy: NonNullable<ReportQuery["groupBy"]>,
) {
  const api = useApi();
  return useQuery<LedgerReport>({
    queryKey: queryKeys.ledgerReport(runId, groupBy),
    queryFn: () => api.automation.ledger.report({ ...(runId ? { runId } : {}), groupBy }),
  });
}

export function useLedgerEfficiency(runId: string | null) {
  const api = useApi();
  return useQuery<RunEfficiency>({
    queryKey: queryKeys.ledgerEfficiency(runId ?? ""),
    queryFn: () => api.automation.ledger.efficiency(runId ?? ""),
    enabled: Boolean(runId),
  });
}

export function useSetRunBudget() {
  const api = useApi();
  return useAutomationMutation(
    ({
      runId,
      tokenBudget,
      action,
    }: {
      runId: string;
      tokenBudget: number | null;
      action?: "pause" | "warn";
    }) => api.automation.ledger.setBudget(runId, tokenBudget, action),
    AUTOMATION_ROOT,
  );
}

export function useCancelDispatch() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, reason }: { id: string; reason?: string }) =>
      api.automation.dispatches.cancel(id, reason),
    AUTOMATION_ROOT,
  );
}

/** The viewer's shape — graph/children/host/statusReason. Polls every 4s while the run is active. */
export function useRunDetail(id: string) {
  const api = useApi();
  return useQuery<RunDetail>({
    queryKey: queryKeys.runDetail(id),
    queryFn: () => api.automation.runs.detail(id),
    enabled: Boolean(id),
    refetchInterval: (result) =>
      result.state.data && isRunLive(result.state.data.run) ? 4_000 : false,
  });
}

export function useRunArtifacts(id: string, live = false) {
  const api = useApi();
  return useQuery<RunArtifact[]>({
    queryKey: queryKeys.runArtifacts(id),
    queryFn: () => api.automation.runs.artifacts(id),
    enabled: Boolean(id),
    refetchInterval: live ? 4_000 : false,
  });
}

export function useRunArtifactContent(id: string, path: string | null, version?: number) {
  const api = useApi();
  return useQuery<ArtifactContent>({
    queryKey: queryKeys.runArtifactContent(id, path ?? ""),
    queryFn: () => api.automation.runs.artifactContent(id, path ?? "", version),
    enabled: Boolean(id) && Boolean(path),
  });
}

export function useStopRun() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, mode, reason }: { id: string; mode: "graceful" | "immediate"; reason?: string }) =>
      api.automation.runs.stop(id, mode, reason),
    RUN_ROOT,
  );
}

export function useStepRun() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, stop }: { id: string; stop?: boolean }) => api.automation.runs.step(id, { stop }),
    RUN_ROOT,
  );
}

export function useJumpRun() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, target }: { id: string; target: JumpRequest }) => api.automation.runs.jump(id, target),
    RUN_ROOT,
  );
}

export function useSetBreakpoints() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, nodeIds }: { id: string; nodeIds: string[] }) =>
      api.automation.runs.breakpoints(id, nodeIds),
    RUN_ROOT,
  );
}

/** The dedicated retry verb — unlike `control(id,"retry")`, it returns the NEW run's id. */
export function useRetryRun() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.automation.runs.retry(id), RUN_ROOT);
}

export function usePublishRun() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, options }: { id: string; options?: PublishOptions }) =>
      api.automation.runs.publish(id, options),
    RUN_ROOT,
  );
}

export function useDirectives(flowId: string) {
  const api = useApi();
  return useQuery<Directive[]>({
    queryKey: queryKeys.directives(flowId),
    queryFn: () => api.automation.directives.list(flowId),
    enabled: Boolean(flowId),
  });
}

export function useCreateDirective() {
  const api = useApi();
  return useAutomationMutation(
    ({ flowId, text }: { flowId: string; text: string }) =>
      api.automation.directives.create(flowId, text),
    AUTOMATION_ROOT,
  );
}

export function useRevokeDirective() {
  const api = useApi();
  return useAutomationMutation(
    (id: string) => api.automation.directives.revoke(id),
    AUTOMATION_ROOT,
  );
}

export function useIdeSessions() {
  const api = useApi();
  return useQuery<IdeSession[]>({
    queryKey: queryKeys.ideSessions,
    queryFn: () => api.automation.ideSessions.list(),
    staleTime: 30_000,
  });
}

export function useCreateDigestTask() {
  const api = useApi();
  return useAutomationMutation(
    (draft: DigestTaskDraft) => api.automation.tasks.createDigest(draft),
    AUTOMATION_ROOT,
  );
}

export function useCreateFlowTask() {
  const api = useApi();
  return useAutomationMutation(
    (draft: FlowTaskDraft) => api.automation.tasks.createFlow(draft),
    AUTOMATION_ROOT,
  );
}

/**
 * The prompt composer AND the Ask bar: propose → confirm → create. This never writes anything
 * itself.
 *
 * A **mutation**, never a query — routing costs a real model call at temperature 0.1, so it
 * must never be re-fetched on focus, retried, or cached under a key that could replay it.
 */
export function useRouteIntent() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({
      prompt,
      surface,
      routeId,
    }: {
      prompt: string;
      surface?: RouteSurface;
      routeId?: string;
    }) => api.automation.tasks.route(prompt, surface, routeId),
    onError,
  });
}

// ── W7: intents (Taskize, notify) and starter templates ──────────────────────

/**
 * Turn prose into confirmable records. Nothing is written by this call — locked decision D2,
 * *Taskize always proposes*; the tray IS the confirm.
 */
export function useTaskize() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: TaskizeInput) => api.intents.taskize(input),
  });
}

/** Publish a workspace notification. Keep the affordance behind `workspaceUpdate` (§E2 R3). */
export function useNotify() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: NotifyInput) => api.intents.notify(input),
    onError,
  });
}

/** Idempotent server-side; invalidates documents because starters ARE documents. */
export function useEnsureStarterTemplates() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: () => api.templates.ensureStarters(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: DOCUMENT_ROOT }),
    onError,
  });
}

// ── Flow health ──────────────────────────────────────────────────────────────

export function useBillingUsage(days = 30) {
  const api = useApi();
  const onError = useErrorToast();
  return useQuery<BillingUsage>({
    queryKey: queryKeys.health(days),
    queryFn: () => api.health.usage(days),
    staleTime: 60_000,
    retry: false,
    throwOnError: (error) => {
      onError(error);
      return false;
    },
  });
}

export function useFlowHealthSettings() {
  const api = useApi();
  return useQuery<FlowHealthSettings>({
    queryKey: queryKeys.healthSettings,
    queryFn: () => api.health.settings(),
  });
}

export function useSaveFlowHealthSettings() {
  const api = useApi();
  return useAutomationMutation(
    (patch: FlowHealthSettingsPatch) => api.health.saveSettings(patch),
    HEALTH_ROOT,
  );
}

export function useDeliveries(query: DeliveryQuery) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.deliveries(query),
    queryFn: () => api.health.deliveries(query),
    placeholderData: (previous) => previous,
  });
}

// ── Document tasks ───────────────────────────────────────────────────────────

export function useDocumentTasks(query: {
  documentId?: string;
  assignedToMe?: boolean;
  includeDone?: boolean;
}) {
  const api = useApi();
  return useQuery<DocumentTask[]>({
    queryKey: queryKeys.documentTasks(query),
    queryFn: () => api.documentTasks.list(query),
    enabled: Boolean(query.documentId) || query.assignedToMe === true,
  });
}

export function useCreateDocumentTask() {
  const api = useApi();
  return useAutomationMutation(
    (input: DocumentTaskInput) => api.documentTasks.create(input),
    DOCUMENT_TASK_ROOT,
  );
}

export function useUpdateDocumentTask() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, patch }: { id: string; patch: DocumentTaskPatch }) =>
      api.documentTasks.update(id, patch),
    DOCUMENT_TASK_ROOT,
  );
}

export function useCompleteDocumentTask() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, done }: { id: string; done: boolean }) => api.documentTasks.complete(id, done),
    DOCUMENT_TASK_ROOT,
  );
}

export function useDeleteDocumentTask() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.documentTasks.remove(id), DOCUMENT_TASK_ROOT);
}

// ── Nudges ───────────────────────────────────────────────────────────────────

export function useNudges(documentId?: string) {
  const api = useApi();
  return useQuery<Nudge[]>({
    queryKey: queryKeys.nudges(documentId),
    queryFn: () => api.nudges.list(documentId),
    enabled: Boolean(documentId),
  });
}

/**
 * Every reminder the viewer created, across every document — the lifecycle surface's list.
 *
 * ⚠ `nudges.list` is **creator-scoped on top of the team filter**: a workspace admin cannot
 * see anybody else's reminders. An empty list is therefore never evidence that the workspace
 * has none, and the surface must say so (§E2 R10).
 */
export function useNudgeInbox(includeResolved = false) {
  const api = useApi();
  return useQuery<Nudge[]>({
    queryKey: [...NUDGE_ROOT, "inbox", includeResolved] as const,
    queryFn: () => api.nudges.list(undefined, includeResolved),
  });
}

export function useCreateNudge() {
  const api = useApi();
  return useAutomationMutation(
    ({ target, when, email }: { target: NudgeTarget; when: QuickWhenResult; email?: boolean }) =>
      api.nudges.create(target, when, email),
    NUDGE_ROOT,
  );
}

export function useSnoozeNudge() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, whenUtc }: { id: string; whenUtc: string }) => api.nudges.snooze(id, whenUtc),
    NUDGE_ROOT,
  );
}

export function useMarkNudgeDone() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.nudges.markDone(id), NUDGE_ROOT);
}

export function useCancelNudge() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.nudges.cancel(id), NUDGE_ROOT);
}

// ── W5: Projects ─────────────────────────────────────────────────────────────

export function useProjects(query?: { status?: ProjectStatus; groupId?: string; limit?: number }) {
  const api = useApi();
  return useQuery<Project[]>({
    queryKey: queryKeys.projects(query),
    queryFn: () => api.projects.list(query),
  });
}

/** The sidebar and the anchor-a-team dialog: projects the viewer can already reach. */
export function useMyProjects() {
  const api = useApi();
  return useQuery<Project[]>({
    queryKey: queryKeys.projectsMine,
    queryFn: () => api.projects.mine(),
  });
}

/**
 * The real per-user ability map (§E-8) — `projects.list` carries none. Fetch once for a page's
 * worth of ids; `usePolicy(project.id)` then finds the result via its cache scan, same as
 * `Groups.tsx` already relies on for its own entities.
 */
export function useProjectAbilities(ids: string[]) {
  const api = useApi();
  return useQuery<EntityAbilities[]>({
    queryKey: queryKeys.projectAbilities(ids),
    queryFn: () => api.projects.abilities(ids),
    enabled: ids.length > 0,
  });
}

export function useProjectPeople(id: string) {
  const api = useApi();
  return useQuery<{ memberships: ProjectMembership[]; users: Member[] }>({
    queryKey: queryKeys.projectPeople(id),
    queryFn: () => api.projects.people.list(id),
    enabled: Boolean(id),
  });
}

export function useProjectGroups(id: string) {
  const api = useApi();
  return useQuery<ProjectGroupMembership[]>({
    queryKey: queryKeys.projectGroups(id),
    queryFn: () => api.projects.people.groups(id),
    enabled: Boolean(id),
  });
}

function useProjectMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      // A project write can move collections in the sidebar (name/group change, archive, an
      // outright delete) — the two roots invalidate together, never one without the other.
      void queryClient.invalidateQueries({ queryKey: PROJECT_ROOT });
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
    },
    onError,
  });
}

export function useCreateProject() {
  const api = useApi();
  return useProjectMutation((input: ProjectDraft) => api.projects.create(input));
}

export function useUpdateProject() {
  const api = useApi();
  return useProjectMutation((input: ProjectPatch & { id: string; etag?: string }) =>
    api.projects.update(input),
  );
}

export function useArchiveProject() {
  const api = useApi();
  return useProjectMutation((id: string) => api.projects.archive(id));
}

export function useRestoreProject() {
  const api = useApi();
  return useProjectMutation((id: string) => api.projects.restore(id));
}

export function useDeleteProject() {
  const api = useApi();
  return useProjectMutation((id: string) => api.projects.delete(id));
}

function useProjectPeopleMutation<TArgs extends { id: string }, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: (_result, args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectPeople(args.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectGroups(args.id) });
    },
    onError,
  });
}

export function useAddProjectPerson() {
  const api = useApi();
  return useProjectPeopleMutation((input: { id: string; userId: string; permission: GrantLevel }) =>
    api.projects.people.add(input),
  );
}

export function useRemoveProjectPerson() {
  const api = useApi();
  return useProjectPeopleMutation((input: { id: string; userId: string }) =>
    api.projects.people.remove(input),
  );
}

export function useAddProjectGroup() {
  const api = useApi();
  return useProjectPeopleMutation(
    (input: { id: string; groupId: string; permission: GrantLevel }) =>
      api.projects.people.addGroup(input),
  );
}

export function useRemoveProjectGroup() {
  const api = useApi();
  return useProjectPeopleMutation((input: { id: string; groupId: string }) =>
    api.projects.people.removeGroup(input),
  );
}

/** The collection menu's "Move to project…" / "Remove from project". */
export function useLinkCollectionProject() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: { collectionId: string; projectId: string | null }) =>
      api.projects.linkCollection(input),
    onSuccess: (collection) => {
      queryClient.setQueryData(queryKeys.collection(collection.id), collection);
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections });
      void queryClient.invalidateQueries({ queryKey: PROJECT_ROOT });
    },
    onError,
  });
}

/** `AnchorProjectDialog` — anchoring (or clearing) the team a project's access rides on. */
export function useAnchorGroupProject() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: { groupId: string; projectId: string | null }) =>
      api.projects.anchorGroup(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
      void queryClient.invalidateQueries({ queryKey: PROJECT_ROOT });
    },
    onError,
  });
}

// ── W5: Docit.Auth administration ─────────────────────────────────────────────

/**
 * `knowitauth.syncUser` is a WRITE (upserts + `[Audited]`) — `enabled: false` so it is never
 * fetched on mount (§E-3); the identity block reads whatever is already in cache and the
 * "Sync now" button is `useSyncAuthIdentity` below, which populates this same cache entry.
 */
export function useAuthIdentity() {
  const api = useApi();
  return useQuery<AuthIdentity>({
    queryKey: queryKeys.authIdentity,
    queryFn: () => api.governance.auth.sync(),
    enabled: false,
  });
}

export function useSyncAuthIdentity() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: () => api.governance.auth.sync(),
    onSuccess: (identity) => queryClient.setQueryData(queryKeys.authIdentity, identity),
    onError,
  });
}

export function useAuthMyTeams() {
  const api = useApi();
  return useQuery<AuthTeamMembership[]>({
    queryKey: queryKeys.authMyTeams,
    queryFn: () => api.governance.auth.myTeams(),
    staleTime: 60_000,
  });
}

export function useAuthTeams() {
  const api = useApi();
  return useQuery<AuthTeam[]>({
    queryKey: queryKeys.authTeams,
    queryFn: () => api.governance.auth.teams.list(),
    staleTime: 60_000,
  });
}

export function useAuthTeamMembers(teamId: string) {
  const api = useApi();
  return useQuery<AuthTeamMember[]>({
    queryKey: queryKeys.authTeamMembers(teamId),
    queryFn: () => api.governance.auth.teams.members(teamId),
    enabled: Boolean(teamId),
  });
}

export function useCreateAuthTeam() {
  const api = useApi();
  return useAutomationMutation(
    (input: { name: string; slug: string; organizationId: string }) =>
      api.governance.auth.teams.create(input),
    AUTH_ROOT,
  );
}

export function useUpdateAuthTeam() {
  const api = useApi();
  return useAutomationMutation(
    (input: { teamId: string; name: string; slug: string }) =>
      api.governance.auth.teams.update(input),
    AUTH_ROOT,
  );
}

export function useDeleteAuthTeam() {
  const api = useApi();
  return useAutomationMutation(
    (teamId: string) => api.governance.auth.teams.remove(teamId),
    AUTH_ROOT,
  );
}

export function useAuthRoles() {
  const api = useApi();
  return useQuery<AuthRole[]>({
    queryKey: queryKeys.authRoles,
    queryFn: () => api.governance.auth.roles.list(),
    staleTime: 60_000,
  });
}

export function useCreateAuthRole() {
  const api = useApi();
  return useAutomationMutation(
    (input: { name: string; slug: string; description?: string; permissions: string[] }) =>
      api.governance.auth.roles.create(input),
    AUTH_ROOT,
  );
}

export function useUpdateAuthRole() {
  const api = useApi();
  return useAutomationMutation(
    (input: {
      roleId: string;
      name: string;
      slug: string;
      description?: string;
      permissions: string[];
    }) => api.governance.auth.roles.update(input),
    AUTH_ROOT,
  );
}

export function useDeleteAuthRole() {
  const api = useApi();
  return useAutomationMutation(
    (roleId: string) => api.governance.auth.roles.remove(roleId),
    AUTH_ROOT,
  );
}

export function useAuthPolicies() {
  const api = useApi();
  return useQuery<AuthPolicy[]>({
    queryKey: queryKeys.authPolicies,
    queryFn: () => api.governance.auth.policies.list(),
    staleTime: 60_000,
  });
}

export interface AuthPolicyDraft {
  name: string;
  description?: string;
  resourceType: string;
  effect: "permit" | "deny";
  priority: number;
  actions: string[];
  conditions: Record<string, unknown>;
  isActive: boolean;
}

export function useCreateAuthPolicy() {
  const api = useApi();
  return useAutomationMutation(
    (input: AuthPolicyDraft) => api.governance.auth.policies.create(input),
    AUTH_ROOT,
  );
}

export function useUpdateAuthPolicy() {
  const api = useApi();
  return useAutomationMutation(
    (input: AuthPolicyDraft & { policyId: string }) => api.governance.auth.policies.update(input),
    AUTH_ROOT,
  );
}

export function useDeleteAuthPolicy() {
  const api = useApi();
  return useAutomationMutation(
    (policyId: string) => api.governance.auth.policies.remove(policyId),
    AUTH_ROOT,
  );
}

// ── W5: Organizations ──────────────────────────────────────────────────────────

export function useOrganizations() {
  const api = useApi();
  return useQuery<Organization[]>({
    queryKey: queryKeys.organizations,
    queryFn: () => api.governance.organizations.list(),
  });
}

export function useCreateOrganization() {
  const api = useApi();
  return useAutomationMutation(
    (input: { name: string; domain?: string; settingsJson?: Record<string, unknown> }) =>
      api.governance.organizations.create(input),
    ORGANIZATION_ROOT,
  );
}

export function useUpdateOrganization() {
  const api = useApi();
  return useAutomationMutation(
    (input: {
      id: string;
      name: string;
      domain?: string;
      settingsJson?: Record<string, unknown>;
    }) => api.governance.organizations.update(input),
    ORGANIZATION_ROOT,
  );
}

export function useDeleteOrganization() {
  const api = useApi();
  return useAutomationMutation(
    (id: string) => api.governance.organizations.remove(id),
    ORGANIZATION_ROOT,
  );
}

// ── W5: Web sources ────────────────────────────────────────────────────────────

export function useSourcePolicy() {
  const api = useApi();
  return useQuery<SourcePolicy>({
    queryKey: queryKeys.sourcePolicy,
    queryFn: () => api.governance.sourcePolicy.get(),
  });
}

/** Every toggle/select in Web sources saves immediately — this IS the save. */
export function useSaveSourcePolicy() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (policy: SourcePolicy) => api.governance.sourcePolicy.save(policy),
    onSuccess: (policy) => queryClient.setQueryData(queryKeys.sourcePolicy, policy),
    onError,
  });
}

export function useSourceApprovals(status?: SourceApprovalStatus) {
  const api = useApi();
  return useQuery<SourceApproval[]>({
    queryKey: queryKeys.sourceApprovals(status),
    queryFn: () => api.governance.sourcePolicy.approvals(status),
  });
}

export function useDecideSourceApproval() {
  const api = useApi();
  return useAutomationMutation(
    (input: { id: string; status: "approved" | "rejected" }) =>
      api.governance.sourcePolicy.decide(input),
    SOURCE_APPROVAL_ROOT,
  );
}

// ── W5: Webhooks ───────────────────────────────────────────────────────────────

export function useWebhooks() {
  const api = useApi();
  return useQuery<Webhook[]>({
    queryKey: queryKeys.webhooks,
    queryFn: () => api.governance.webhooks.list(),
  });
}

export function useWebhookDeliveries(id: string) {
  const api = useApi();
  return useQuery<WebhookDelivery[]>({
    queryKey: queryKeys.webhookDeliveries(id),
    queryFn: () => api.governance.webhooks.deliveries(id),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useCreateWebhook() {
  const api = useApi();
  return useAutomationMutation(
    (input: WebhookDraft) => api.governance.webhooks.create(input),
    WEBHOOK_ROOT,
  );
}

export function useUpdateWebhook() {
  const api = useApi();
  return useAutomationMutation(
    (input: WebhookDraft & { id: string }) => api.governance.webhooks.update(input),
    WEBHOOK_ROOT,
  );
}

export function useDeleteWebhook() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.governance.webhooks.remove(id), WEBHOOK_ROOT);
}

/** Returns the DTO with the REAL new secret — a one-time reveal. Never cached, never logged. */
export function useRotateWebhookSecret() {
  const api = useApi();
  return useAutomationMutation(
    (id: string) => api.governance.webhooks.rotateSecret(id),
    WEBHOOK_ROOT,
  );
}

// ── W5: Members / Groups writes (clean-room, §A.6) ─────────────────────────────

function useMembersMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.members }),
    onError,
  });
}

export function useInviteMembers() {
  const api = useApi();
  return useMembersMutation((invites: MemberInvite[]) => api.people.invite(invites));
}

export function useResendInvite() {
  const api = useApi();
  return useMembersMutation((id: string) => api.people.resendInvite(id));
}

export function useSetMemberRole() {
  const api = useApi();
  return useMembersMutation((input: { id: string; role: MemberRole }) => api.people.setRole(input));
}

export function useSuspendMember() {
  const api = useApi();
  return useMembersMutation((id: string) => api.people.suspend(id));
}

export function useActivateMember() {
  const api = useApi();
  return useMembersMutation((id: string) => api.people.activate(id));
}

export function useRemoveMember() {
  const api = useApi();
  return useMembersMutation((id: string) => api.people.remove(id));
}

function useGroupsMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.groups }),
    onError,
  });
}

export function useCreateGroup() {
  const api = useApi();
  return useGroupsMutation((input: { name: string; externalId?: string }) =>
    api.people.groups.create(input),
  );
}

export function useUpdateGroup() {
  const api = useApi();
  return useGroupsMutation((input: { id: string; name?: string; externalId?: string }) =>
    api.people.groups.update(input),
  );
}

export function useDeleteGroup() {
  const api = useApi();
  return useGroupsMutation((id: string) => api.people.groups.delete(id));
}

export function useGroupMembers(id: string) {
  const api = useApi();
  return useQuery<{ memberships: GroupMembership[]; users: Member[] }>({
    queryKey: queryKeys.groupMembers(id),
    queryFn: () => api.people.groups.members(id),
    enabled: Boolean(id),
  });
}

function useGroupMembershipMutation<TArgs extends { id: string }, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: (_result, args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groupMembers(args.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
    onError,
  });
}

export function useAddGroupMember() {
  const api = useApi();
  return useGroupMembershipMutation((input: { id: string; userId: string }) =>
    api.people.groups.addMember(input),
  );
}

export function useRemoveGroupMember() {
  const api = useApi();
  return useGroupMembershipMutation((input: { id: string; userId: string }) =>
    api.people.groups.removeMember(input),
  );
}

export function useSetGroupMemberRole() {
  const api = useApi();
  return useGroupMembershipMutation(
    (input: { id: string; userId: string; role: GroupMemberRole }) =>
      api.people.groups.setMemberRole(input),
  );
}

// ── W4: connected editor windows, dispatch, grants, doc sync, conversations ──────────────
//
// Every cadence below is kms's own, transferred rather than re-guessed, because each one was
// chosen against a real constraint: the extension heartbeats every 30 s (so 60 s sees every
// window within two beats), the dispatch result poll matches `ideDispatchPoll.ts`'s 2 s, and
// the conversation poll is the FALLBACK cadence kms uses when its SignalR socket is down —
// which, in slate, is always (§E-3: poll now, socket later).

/** A dispatch in one of these states can still change on its own; a terminal one cannot. */
const LIVE_DISPATCH_STATUSES = new Set(["queued", "claimed", "running"]);

export function isDispatchLive(dispatch: Pick<Dispatch, "status">): boolean {
  return LIVE_DISPATCH_STATUSES.has(dispatch.status);
}

/**
 * The workspace's connected editor windows.
 *
 * 60 s, matching kms exactly — and deliberately NOT faster: liveness is SERVER-computed
 * (`isOnline` from `lastSeenAt` against a 90 s TTL), so polling harder would not make a window
 * go offline any sooner, it would only cost requests.
 */
export function useIdeSessionsPage(includeRevoked = false, refetchMs = 60_000) {
  const api = useApi();
  return useQuery<IdeSessionsPage>({
    queryKey: queryKeys.ideSessionsPage(includeRevoked),
    queryFn: () => api.ide.sessions.list({ includeRevoked }),
    // The cadence belongs to the OBSERVER, not the cache entry — which is why the wizard's
    // step 3 can ask at 5 s (somebody is standing there waiting for a window to appear) while
    // the console keeps 60 s, with both sharing one request and one cache.
    refetchInterval: refetchMs,
  });
}

/**
 * The caller's OWN windows across every workspace they belong to, each with the workspace it is
 * registered in — the header's model chooser (a window is the person's, not the workspace's).
 */
export function useMyIdeWindows(refetchMs = 30_000) {
  const api = useApi();
  return useQuery<IdeSessionsPage>({
    queryKey: queryKeys.myIdeWindows,
    queryFn: () => api.ide.sessions.list({ allWorkspaces: true }),
    refetchInterval: refetchMs,
  });
}

/**
 * One window's five bands. 20 s, and deliberately not a second realtime channel.
 *
 * There is no store behind this on purpose: holding somebody's prompts in memory after they
 * navigated away is not a trade this feature should make, so the only copy is React Query's,
 * keyed by session and dropped with the page.
 */
export function useIdeSessionHistory(sessionKey: string | undefined) {
  const api = useApi();
  return useQuery<IdeSessionHistory>({
    queryKey: queryKeys.ideSessionHistory(sessionKey ?? ""),
    queryFn: () => api.ide.sessions.history(sessionKey ?? ""),
    enabled: Boolean(sessionKey),
    refetchInterval: 20_000,
  });
}

function useIdeMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  ...roots: ReadonlyArray<readonly unknown[]>
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      for (const root of roots) {
        void queryClient.invalidateQueries({ queryKey: root });
      }
    },
    onError,
  });
}

export function useRevokeIdeSession() {
  const api = useApi();
  return useIdeMutation(
    (input: { id?: string; sessionKey?: string }) => api.ide.sessions.revoke(input),
    ["ide", "sessions"],
  );
}

/**
 * "Ask this window." Invalidates the dispatch keys rather than the session keys: asking does
 * not change the window, it creates work — and the work is what the reader is now waiting on.
 */
export function useCreateDispatch() {
  const api = useApi();
  return useIdeMutation(
    (input: DispatchDraft) => api.ide.dispatches.create(input),
    ["automation", "dispatches"],
  );
}

/**
 * One dispatch, polled at 2 s while it is still moving and never once it has stopped —
 * `ideDispatchPoll.ts`'s own `POLL_INTERVAL_MS`. The predicate reads `statusDetail` first
 * because `awaiting-approval` and `unreachable-window` are live states that `status` cannot
 * express: a row parked on the IDE's consent prompt is `Claimed`, and stopping the poll there
 * would leave the surface frozen at the exact moment somebody is answering it.
 */
export function useDispatch(id: string | undefined) {
  const api = useApi();
  return useQuery<Dispatch>({
    queryKey: queryKeys.dispatch(id ?? ""),
    queryFn: () => api.ide.dispatches.get(id ?? ""),
    enabled: Boolean(id),
    refetchInterval: (result) => {
      const dispatch = result.state.data;
      if (!dispatch) {
        return 2_000;
      }
      const detail = dispatch.statusDetail;
      const live =
        detail === null
          ? isDispatchLive(dispatch)
          : ["queued", "running", "awaiting-approval"].includes(detail);
      return live ? 2_000 : false;
    },
  });
}

/**
 * The lines a window wrote for one of the caller's dispatches — read ONCE, when the dispatch has
 * ended (`enabled`). A finished dispatch's lines do not change, so it is never refetched.
 */
export function useDispatchOutput(id: string | undefined, enabled: boolean) {
  const api = useApi();
  return useQuery<DispatchOutput>({
    queryKey: queryKeys.dispatchOutput(id ?? ""),
    queryFn: () => api.ide.dispatches.output(id ?? ""),
    enabled: Boolean(id) && enabled,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

/**
 * Which window each of the caller's conversations is held with (desk `/talk`). It lives under the
 * dispatch root on purpose: sending a turn invalidates `["automation", "dispatches"]`, and a turn
 * is exactly what moves a thread. 30 s otherwise — a turn sent from another tab still shows up.
 */
export function useTalkThreads() {
  const api = useApi();
  return useQuery<TalkThread[]>({
    queryKey: queryKeys.talkThreads,
    queryFn: () => api.ide.dispatches.threads(),
    refetchInterval: 30_000,
    retry: false,
  });
}

/**
 * Grants. **No interval**: a grant changes when somebody presses a button, and every mutation
 * below invalidates this key — polling would be asking a question whose answer only a person
 * can change.
 */
export function useIdeGrants() {
  const api = useApi();
  return useQuery<IdeGrantList>({
    queryKey: queryKeys.ideGrants,
    queryFn: () => api.ide.grants.list(),
  });
}

export function useIdeGrantCandidates(enabled = true) {
  const api = useApi();
  return useQuery<IdeGrantCandidate[]>({
    queryKey: queryKeys.ideGrantCandidates,
    queryFn: () => api.ide.grants.candidates(),
    enabled,
    retry: false,
  });
}

/** Only while the row is expanded — an empty trail is not worth a request per render. */
export function useIdeGrantActivity(grantId: string | null) {
  const api = useApi();
  return useQuery<IdeGrantActivity[]>({
    queryKey: queryKeys.ideGrantActivity(grantId ?? ""),
    queryFn: () => api.ide.grants.activity(grantId ?? ""),
    enabled: Boolean(grantId),
  });
}

export function useRequestIdeGrant() {
  const api = useApi();
  return useIdeMutation(
    (input: { targetUserId: string; purpose: string }) => api.ide.grants.request(input),
    queryKeys.ideGrants,
  );
}

export function useApproveIdeGrant() {
  const api = useApi();
  return useIdeMutation(
    (input: IdeGrantApproval) => api.ide.grants.approve(input),
    queryKeys.ideGrants,
  );
}

export function useDeclineIdeGrant() {
  const api = useApi();
  return useIdeMutation((grantId: string) => api.ide.grants.decline(grantId), queryKeys.ideGrants);
}

export function useRevokeIdeGrant() {
  const api = useApi();
  return useIdeMutation((grantId: string) => api.ide.grants.revoke(grantId), queryKeys.ideGrants);
}

/**
 * Sync links, at 60 s. The whole point of the surface is watching ONE timestamp move: a link
 * whose `lastSyncAt` has stopped advancing is the only observable of the silent-drift failure
 * (§E-1), and a static page would hide the very thing it exists to show.
 */
export function useDocSyncLinks(collectionId?: string) {
  const api = useApi();
  return useQuery<DocSyncLink[]>({
    queryKey: [...queryKeys.docSyncLinks, collectionId ?? ""],
    queryFn: () => api.docSync.links(collectionId),
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useDocSyncState(linkId: string | null) {
  const api = useApi();
  return useQuery<DocSyncStatePage>({
    queryKey: queryKeys.docSyncState(linkId ?? ""),
    queryFn: () => api.docSync.state(linkId ?? ""),
    enabled: Boolean(linkId),
  });
}

export function useUpdateDocSyncLink() {
  const api = useApi();
  return useIdeMutation(
    (input: { link: DocSyncLink; patch: DocSyncLinkFlags }) =>
      api.docSync.updateLink(input.link, input.patch),
    queryKeys.docSyncLinks,
  );
}

export function useConversations() {
  const api = useApi();
  return useQuery<ConversationSummary[]>({
    queryKey: queryKeys.conversations,
    queryFn: () => api.conversations.list(),
    retry: false,
  });
}

/**
 * One thread. 5 s **only while a reply is pending** — kms's own fallback cadence for exactly
 * this case, and the honest replacement for the SignalR group slate does not join (§E-3).
 * Polling a finished conversation would be asking a question with no asker.
 */
export function useConversation(id: string | null, awaitingReply = false) {
  const api = useApi();
  return useQuery<ConversationDetail>({
    queryKey: queryKeys.conversation(id ?? ""),
    queryFn: () => api.conversations.get(id ?? ""),
    enabled: Boolean(id),
    refetchInterval: awaitingReply ? 5_000 : false,
  });
}

export function useCreateConversation() {
  const api = useApi();
  return useIdeMutation(
    (input: ConversationDraft) => api.conversations.create(input),
    queryKeys.conversations,
  );
}

/** `staleTime: Infinity` — a server's config flag does not change while somebody onboards. */
export function useWizardCapabilities(enabled = true) {
  const api = useApi();
  return useQuery<WizardCapabilities>({
    queryKey: queryKeys.wizardCapabilities,
    queryFn: () => api.wizard.capabilities(),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export function useWizardPreflight(enabled = true) {
  const api = useApi();
  return useQuery<WizardPreflight>({
    queryKey: queryKeys.wizardPreflight,
    queryFn: () => api.wizard.preflight(),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * §E-5, at the call site. The merge lives in the adapter, but the INVALIDATION lives here:
 * a preference write changes `workspaces.current()`, and a switch left rendering the old
 * cached value would look like the write failed.
 */
export function useUpdateWorkspacePreferences() {
  const api = useApi();
  return useIdeMutation(
    (patch: Record<string, unknown>) => api.workspaces.updatePreferences(patch),
    queryKeys.workspaces,
    queryKeys.currentWorkspace,
  );
}

// ── W6: the v2 editing lane ──────────────────────────────────────────────────

/**
 * Which lane a document is on, plus the room name and the frozen v2 projections.
 *
 * ⚠ `contentV2`/`textV2` come back STALE for any document edited in the v2 lane: the
 * collaboration server's markdown projection throws on every v2-only node, so `v2room.js`
 * writes `stateV2` and skips the other two (W6 §0.3 / R1, program blocker B5). Treat them as
 * "what the server last managed to project", never as the live document — the live document
 * is the CRDT room.
 */
export function useEditorV2Lane(documentId: string, enabled = true) {
  const api = useApi();
  return useQuery<EditorV2Lane>({
    queryKey: queryKeys.editorV2Lane(documentId),
    queryFn: () => api.editorV2.get(documentId),
    enabled: enabled && documentId.length > 0,
    staleTime: 30_000,
  });
}

/**
 * The collaboration JWT — a DIFFERENT token from this app's PKCE bearer, HS256 and valid for
 * 24 hours. Refetched well inside that window, because a provider built on an expired token
 * silently drops to read-only rather than failing (W6 R8).
 */
export function useCollaborationToken(enabled = true) {
  const api = useApi();
  return useQuery<string | null>({
    queryKey: queryKeys.collaborationToken,
    queryFn: () => api.editorV2.collaborationToken(),
    enabled,
    staleTime: 60 * 60_000,
    refetchInterval: 6 * 60 * 60_000,
    retry: false,
  });
}

export function useOptInToV2() {
  const api = useApi();
  return useDocumentMutation<string, EditorV2OptIn>((documentId) => api.editorV2.optIn(documentId));
}

export function useRollbackFromV2() {
  const api = useApi();
  return useDocumentMutation<string, { lane: "v1" }>((documentId) =>
    api.editorV2.rollback(documentId),
  );
}

/**
 * The dual-ACL gate. Deliberately NOT wrapped in `useDocumentMutation`: a refusal resolves to
 * `null` rather than throwing, so there is no error to toast — the caller re-asks on the
 * propose lane, which is the designed negotiation, not a failure.
 */
export function useV2AiAction() {
  const api = useApi();
  return useMutation<
    AiActionDecision | null,
    Error,
    { documentId: string; request: AiActionRequest }
  >({
    mutationFn: ({ documentId, request }) => api.editorV2.aiAction(documentId, request),
  });
}

export function useV2Commit() {
  const api = useApi();
  return useMutation<
    EditorV2Commit,
    Error,
    { documentId: string; range: { from: number; to: number } }
  >({
    mutationFn: ({ documentId, range }) => api.editorV2.commit(documentId, range),
  });
}

export function useV2Derive() {
  const api = useApi();
  return useMutation<
    DeriveOutcome,
    Error,
    { documentId: string; request: { text: string; from: number; to: number } }
  >({
    mutationFn: ({ documentId, request }) => api.editorV2.derive(documentId, request),
  });
}

/**
 * The merged, SCOPED action catalogue the in-editor menus render. The unscoped `useAiActions`
 * cannot see team/project/document-scoped actions at all — it merges two lists client-side.
 */
export function useScopedAiActions(query: ScopedActionQuery, enabled = true) {
  const api = useApi();
  return useQuery<SelectionAction[]>({
    queryKey: queryKeys.scopedAiActions(query),
    queryFn: () => api.aiActions.listScoped(query),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Run one selection action. The outcome is a UNION: text, or a dispatch id to poll because the
 * resolved thinker is pinned to a VS Code window — collapsing the second into an error is how
 * a ten-minute round trip becomes "the AI did not return a result".
 */
export function useTransformSelection() {
  const api = useApi();
  return useMutation<TransformOutcome, Error, TransformRequest>({
    mutationFn: (request) => api.selection.transform(request),
  });
}

/** One poll of an IDE-pinned dispatch. The caller decides when to stop via `dispatchSettled`. */
export function useIdeDispatchResult() {
  const api = useApi();
  return useMutation<IdeDispatch, Error, string>({
    mutationFn: (dispatchId) => api.selection.dispatchResult(dispatchId),
  });
}

/**
 * The streaming refine conversation. `signal` is not optional in spirit: without it an
 * abandoned refinement keeps streaming into a closed drawer, which is the one thing the kms
 * implementation cannot do (§F6.10).
 */
export function useRefineSelection() {
  const api = useApi();
  return useMutation<
    void,
    Error,
    { request: RefineRequest; onEvent: (event: RefineEvent) => void; signal?: AbortSignal }
  >({
    mutationFn: ({ request, onEvent, signal }) => api.selection.refine(request, onEvent, signal),
  });
}

export function useMentionSuggestions(query: string, enabled = true) {
  const api = useApi();
  return useQuery<MentionSuggestions>({
    queryKey: queryKeys.mentionSuggestions(query),
    queryFn: () => api.mentions.search(query),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Live values for a widget that declares a `sourceRef`. `{available:false}` is a normal answer
 * — no provider is configured — so this never retries it into a failure.
 */
export function useQuoteSnapshot(symbols: string[], enabled = true) {
  const api = useApi();
  return useQuery<QuoteSnapshot>({
    queryKey: queryKeys.quoteSnapshot(symbols),
    queryFn: () => api.quotes.get(symbols),
    enabled: enabled && symbols.length > 0,
    staleTime: 60_000,
    retry: false,
  });
}

// ── W9: wisdom graph, merge, share grants, profiles, notifications ─────────────────────────────

export function useWisdomSearch(
  query: string,
  options: { kinds?: string[]; k?: number; filters?: WisdomFilters; enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery<WisdomSearchResult>({
    queryKey: queryKeys.wisdomSearch(query, options.filters),
    queryFn: () =>
      api.wisdom.search({ query, kinds: options.kinds, k: options.k, filters: options.filters }),
    enabled: (options.enabled ?? true) && query.trim().length > 0,
    // A 429 means "you already asked three times this minute" — retrying it into a fourth
    // request is exactly the storm the server's rate limit exists to stop (§A.1 W14).
    retry: false,
  });
}

export function useWisdomNode(kind: string, id: string, enabled = true) {
  const api = useApi();
  return useQuery<WisdomNode | null>({
    queryKey: queryKeys.wisdomNode(kind, id),
    queryFn: () => api.wisdom.node({ id, kind }),
    enabled: enabled && Boolean(id) && Boolean(kind),
  });
}

export function useWisdomNeighbors(
  kind: string,
  id: string,
  depth: 1 | 2 = 1,
  rels: string[] = [],
  enabled = true,
) {
  const api = useApi();
  return useQuery<WisdomNeighborhood | null>({
    queryKey: queryKeys.wisdomNeighbors(kind, id, depth, rels.join(",")),
    queryFn: () => api.wisdom.neighbors({ id, kind, depth, rels }),
    enabled: enabled && Boolean(id) && Boolean(kind),
  });
}

export function useWisdomProvenance(kind: string, id: string, enabled = true) {
  const api = useApi();
  return useQuery<WisdomProvenance | null>({
    queryKey: queryKeys.wisdomProvenance(kind, id),
    queryFn: () => api.wisdom.provenance({ id, kind }),
    enabled: enabled && Boolean(id) && Boolean(kind),
  });
}

// ── CANOPY (CNPY-8): the knowledge tree, read level by level ─────────────────────────────────

/** The workspace's canopy scopes, with live counts. 60 s: the drain moves the counts slowly. */
export function useCanopyScopes() {
  const api = useApi();
  return useQuery<CanopyScope[]>({
    queryKey: ["canopy", "scopes"],
    queryFn: () => api.canopy.scopes(),
    refetchInterval: 60_000,
    retry: false,
  });
}

/**
 * One canopy node (id, or scope + path). Not polled: a page of a tree is read, not watched — the
 * page offers a refresh, and reading a stale node already boosts it server-side.
 */
export function useCanopyNode(query: CanopyNodeQuery | null) {
  const api = useApi();
  return useQuery<CanopyNodeView | null>({
    queryKey: [
      "canopy",
      "node",
      query?.id ?? "",
      query?.scope ?? "",
      query?.path ?? "",
      query?.offset ?? 0,
    ],
    queryFn: () => api.canopy.node(query ?? {}),
    enabled: query !== null,
    retry: false,
  });
}

export function useCanopyFind(query: string, scope?: string) {
  const api = useApi();
  const q = query.trim();
  return useQuery<CanopyFindResult>({
    queryKey: ["canopy", "find", scope ?? "", q],
    queryFn: () => api.canopy.find({ query: q, scope, k: 15 }),
    enabled: q.length >= 2,
    retry: false,
  });
}

/** A leaf's raw words, on demand (an open is audited server-side, so it never fires on render). */
export function useCanopyOpen() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { id: string; maxTokens?: number }) => api.canopy.open(input),
  });
}

/** Desk `/knowledge/debug` — the pipeline report. 30 s while open: the drain moves the numbers. */
export function useKnowledgeDebug(scope?: string) {
  const api = useApi();
  return useQuery<KnowledgeDebugReport>({
    queryKey: ["knowledge", "debug", scope ?? ""],
    queryFn: () => api.knowledgeDebug.report(scope),
    refetchInterval: 30_000,
    retry: false,
  });
}

/** The tree map window: a node (default the root) and `depth` levels under it, each with subtree counts. */
export function useKnowledgeDebugTree(
  scope: string | undefined,
  nodeId: string | undefined,
  depth: number,
) {
  const api = useApi();
  return useQuery<DebugTree | null>({
    queryKey: ["knowledge", "debug", "tree", scope ?? "", nodeId ?? "", depth],
    queryFn: () => api.knowledgeDebug.tree({ scope, nodeId, depth }),
    retry: false,
  });
}

// ── Workspace Knowledge (`/knowledge`) ───────────────────────────────────────────────────────────

/** How often the area asks again — ONLY while a refresh is live; otherwise it does not poll. */
export const KNOWLEDGE_POLL_MS = 2_000;

export const knowledgeKeys = {
  status: ["knowledge", "status"] as const,
  activity: (limit: number) => ["knowledge", "activity", limit] as const,
};

/** The six stages, derived now. Polls every 2 s while `running` is set, never otherwise. */
export function useKnowledgeStatus() {
  const api = useApi();
  return useQuery<KnowledgeStatus>({
    queryKey: knowledgeKeys.status,
    queryFn: () => api.knowledge.status(),
    refetchInterval: (query) => (query.state.data?.running ? KNOWLEDGE_POLL_MS : false),
    retry: false,
  });
}

/**
 * The workspace's refreshes, newest first. Polls while `live` (the status says a run is going) or
 * while its own list still shows one running — so the final outcome sentence is always fetched.
 */
export function useKnowledgeActivity(limit = 20, live = false) {
  const api = useApi();
  return useQuery<KnowledgeRun[]>({
    queryKey: knowledgeKeys.activity(limit),
    queryFn: () => api.knowledge.activity(limit),
    refetchInterval: (query) =>
      live || (query.state.data ?? []).some(isLiveRun) ? KNOWLEDGE_POLL_MS : false,
    retry: false,
  });
}

/** Start a refresh. The result is typed (started / already running / refused / forbidden). */
export function useKnowledgeRefresh() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    KnowledgeRefreshResult,
    Error,
    { stage: KnowledgeRefreshTarget; force?: boolean }
  >({
    mutationFn: ({ stage, force }) => api.knowledge.refresh(stage, force),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["knowledge", "status"] });
      void queryClient.invalidateQueries({ queryKey: ["knowledge", "activity"] });
    },
  });
}

export function useKnowledgeCancel() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<KnowledgeRunState, Error, string>({
    mutationFn: (runId) => api.knowledge.cancel(runId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["knowledge", "status"] });
      void queryClient.invalidateQueries({ queryKey: ["knowledge", "activity"] });
    },
  });
}

/**
 * The project narrated top-down. `retry: false`: a 4xx here is a statement (no workspace, no
 * graph), not a blip — and the server's own `degraded`/`causality` fields explain absence.
 */
export function useWisdomTopDown(
  input: { scope?: "workspace" | "component"; key?: string; depth?: number } = {},
  enabled = true,
) {
  const api = useApi();
  return useQuery<WisdomTopDown>({
    queryKey: ["wisdom", "topdown", input.scope ?? "workspace", input.key ?? "", input.depth ?? 3],
    queryFn: () => api.wisdom.topdown(input),
    enabled,
    retry: false,
  });
}

/** Drift and stale pairs — the instrument the top-down view's drift panel reads. */
export function useWisdomCoverage(enabled = true) {
  const api = useApi();
  return useQuery<WisdomCoverage>({
    queryKey: ["wisdom", "coverage-code"],
    queryFn: () => api.wisdom.coverageCode(),
    enabled,
    retry: false,
  });
}

export function useWisdomOverview(enabled = true) {
  const api = useApi();
  return useQuery<WisdomOverview>({
    queryKey: queryKeys.wisdomOverview,
    queryFn: () => api.wisdom.overview(),
    enabled,
  });
}

/** New in slate (§A.1 W9) — the path finder between two pinned nodes. A mutation, not a query:
 * it fires on demand from an explicit "find the route" action, not on every keystroke. */
export function useWisdomPath() {
  const api = useApi();
  return useMutation<
    Awaited<ReturnType<typeof api.wisdom.path>>,
    Error,
    {
      fromId: string;
      toId: string;
      fromKind: string;
      toKind: string;
      maxLen?: number;
      rels?: string[];
    }
  >({
    mutationFn: (input) => api.wisdom.path(input),
  });
}

export function useExperts(query: string, enabled = true) {
  const api = useApi();
  return useQuery<Expert[]>({
    queryKey: queryKeys.wisdomExperts(query),
    queryFn: () => api.wisdom.experts(query),
    enabled: enabled && query.trim().length > 0,
  });
}

// ── Merge + revisions ────────────────────────────────────────────────────────────────────────

export function useDivergence(documentId: string, enabled = true) {
  const api = useApi();
  return useQuery<Divergence>({
    queryKey: queryKeys.divergence(documentId),
    queryFn: () => api.merge.divergence(documentId),
    enabled: enabled && Boolean(documentId),
    // M2: kms swallows a failed divergence check and shows nothing; slate does the same but
    // never caches the failure, so the badge tries again on the next visit rather than staying
    // silently stuck.
    retry: false,
    gcTime: 0,
  });
}

export function useMergeSession(sessionId: string | null) {
  const api = useApi();
  return useQuery<MergePartDiff[]>({
    queryKey: queryKeys.mergeSession(sessionId ?? ""),
    queryFn: () => api.merge.preview(sessionId ?? ""),
    enabled: Boolean(sessionId),
  });
}

export function useOpenMerge() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation<
    MergeSession,
    Error,
    { documentId: string; contributingRevisionIds: string[] }
  >({
    mutationFn: (input) => api.merge.open(input),
    onError,
  });
}

export function useResolveMergePart() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<
    void,
    Error,
    {
      sessionId: string;
      partId: string;
      decision: MergeDecision;
      chosenFrom?: string;
      combinedContent?: string;
    }
  >({
    mutationFn: (input) => api.merge.resolvePart(input),
    onSuccess: (_result, input) =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.mergeSession(input.sessionId) }),
    onError,
  });
}

export function useCommitMerge(documentId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<MergeCommitResult, Error, string>({
    mutationFn: (sessionId) => api.merge.commit(sessionId),
    onSuccess: () => {
      // M8: invalidate the document, its revisions AND its divergence — kms's badge stayed
      // stale until History remounted; this is the one line that fixes it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.document(documentId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.revisions(documentId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.divergence(documentId) });
    },
    // No onError toast here — MergeUnresolvedError carries a count the panel renders inline,
    // and a generic toast on top of that would say the same thing twice.
  });
}

export function useMergeAudit(sessionId: string | null) {
  const api = useApi();
  return useQuery<MergeAudit>({
    queryKey: [...queryKeys.mergeSession(sessionId ?? ""), "audit"] as const,
    queryFn: () => api.merge.audit(sessionId ?? ""),
    enabled: Boolean(sessionId),
  });
}

export function useRevisionParts(revisionId: string | null) {
  const api = useApi();
  return useQuery<RevisionPart[]>({
    queryKey: queryKeys.revisionParts(revisionId ?? ""),
    // M11: skipped for the synthetic "latest" id.
    queryFn: () => api.merge.revisionParts(revisionId ?? ""),
    enabled: Boolean(revisionId) && revisionId !== "latest",
  });
}

export function useRestoreRevision(documentId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<
    { restoredRevisionId: string },
    Error,
    { revisionId: string; partId?: string }
  >({
    mutationFn: (input) => api.merge.restoreRevision({ documentId, ...input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.document(documentId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.revisions(documentId) });
    },
    onError,
  });
}

// ── Share grants ─────────────────────────────────────────────────────────────────────────────

export function useShareGrants(documentId: string, enabled = true) {
  const api = useApi();
  return useQuery<ShareGrant[]>({
    queryKey: queryKeys.grants(documentId),
    queryFn: () => api.grants.list(documentId),
    enabled: enabled && Boolean(documentId),
  });
}

export function useCreateShareGrant() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<ShareGrant, Error, ShareGrantDraft>({
    mutationFn: (input) => api.grants.create(input),
    onSuccess: (grant) =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.grants(grant.documentId) }),
    onError,
  });
}

export function useUpdateShareGrant(documentId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<ShareGrant, Error, ShareGrantPatch & { id: string }>({
    mutationFn: (input) => api.grants.update(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.grants(documentId) }),
    onError,
  });
}

export function useRevokeShareGrant(documentId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.grants.revoke(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.grants(documentId) }),
    onError,
  });
}

/** The public door's read — never retried into a `gone` link three times (§C.4). */
export function useGrantDocument(urlId: string, password?: string) {
  const api = useApi();
  return useQuery<GrantRead>({
    queryKey: [...queryKeys.grantRead(urlId), password ?? ""] as const,
    queryFn: () => api.grants.read({ urlId, password }),
    enabled: Boolean(urlId),
    retry: false,
  });
}

export function useGrantComments(urlId: string, password?: string, enabled = true) {
  const api = useApi();
  return useQuery<GrantComment[]>({
    queryKey: queryKeys.grantComments(urlId),
    queryFn: () => api.grants.comments({ urlId, password }),
    enabled: enabled && Boolean(urlId),
    retry: false,
  });
}

export function usePostGrantComment(urlId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<GrantComment, Error, { password?: string; authorName?: string; text: string }>(
    {
      mutationFn: (input) => api.grants.comment({ urlId, ...input }),
      onSuccess: () =>
        void queryClient.invalidateQueries({ queryKey: queryKeys.grantComments(urlId) }),
    },
  );
}

// ── Profiles ─────────────────────────────────────────────────────────────────────────────────

export function useOwnProfile() {
  const api = useApi();
  return useQuery<ProfileCv>({ queryKey: queryKeys.profileOwn, queryFn: () => api.profiles.own() });
}

export function useUpdateProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ProfileCv, Error, ProfilePatch>({
    mutationFn: (patch) => api.profiles.update(patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.profileOwn }),
    // No generic error toast: a slug collision (`ConflictError`) reads better inline, next to
    // the field that caused it, than as a toast that has already scrolled away.
  });
}

export function useUpsertSkill() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<
    ProfileCv,
    Error,
    { name: string; level?: SkillLevel; projectsUsedCount?: number }
  >({
    mutationFn: (input) => api.profiles.upsertSkill(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.profileOwn }),
    onError,
  });
}

export function useRemoveSkill() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<ProfileCv, Error, string>({
    mutationFn: (skillId) => api.profiles.removeSkill(skillId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.profileOwn }),
    onError,
  });
}

export function useSetPortfolio() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<ProfileCv, Error, PortfolioEntry[]>({
    mutationFn: (entries) => api.profiles.setPortfolio(entries),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.profileOwn }),
    onError,
  });
}

export function useExportCv() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation<Awaited<ReturnType<typeof api.profiles.exportCv>>, Error, void>({
    mutationFn: () => api.profiles.exportCv(),
    onError,
  });
}

/**
 * The system CV interview. One query for where the caller stands, and mutations that each answer
 * the WHOLE sitting — the server owns the script, so every result simply replaces the cached
 * session rather than being merged into a client-side state machine.
 */
export function useCvInterview() {
  const api = useApi();
  return useQuery<CvInterviewState>({
    queryKey: queryKeys.cvInterview,
    queryFn: () => api.profiles.interview.get(),
  });
}

/** Every mutation lands the same way: the sitting it answered with becomes the cached sitting. */
function useInterviewWriter<TVariables>(
  mutationFn: (variables: TVariables) => Promise<CvInterview>,
  onError?: (error: Error) => void,
) {
  const queryClient = useQueryClient();
  return useMutation<CvInterview, Error, TVariables>({
    mutationFn,
    onSuccess: (interview) => {
      queryClient.setQueryData<CvInterviewState>(queryKeys.cvInterview, (previous) =>
        previous ? { ...previous, interview } : previous,
      );
      if (!queryClient.getQueryData(queryKeys.cvInterview)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.cvInterview });
      }
    },
    ...(onError ? { onError } : {}),
  });
}

export function useStartCvInterview() {
  const api = useApi();
  const onError = useErrorToast();
  return useInterviewWriter<void>(() => api.profiles.interview.start(), onError);
}

export function useAnswerCvInterview() {
  const api = useApi();
  // No toast: a scoring pass that could not finish is answerable again in place, and the scene
  // says so next to the question rather than in a corner of the screen.
  return useInterviewWriter<{ interviewId: string; text: string; telemetry?: AnswerTelemetry }>(
    (input) => api.profiles.interview.answer(input),
  );
}

export function useAmendCvInterview() {
  const api = useApi();
  const onError = useErrorToast();
  return useInterviewWriter<{ interviewId: string; turnIndex: number; text: string }>(
    (input) => api.profiles.interview.amend(input),
    onError,
  );
}

export function useEditCvDraft() {
  const api = useApi();
  const onError = useErrorToast();
  return useInterviewWriter<{ interviewId: string; field: string; value: string | null }>(
    (input) => api.profiles.interview.editDraft(input),
    onError,
  );
}

export function usePauseCvInterview() {
  const api = useApi();
  const onError = useErrorToast();
  return useInterviewWriter<string>((id) => api.profiles.interview.pause(id), onError);
}

export function useResumeCvInterview() {
  const api = useApi();
  const onError = useErrorToast();
  return useInterviewWriter<string>((id) => api.profiles.interview.resume(id), onError);
}

export function useDiscardCvInterview() {
  const api = useApi();
  const onError = useErrorToast();
  return useInterviewWriter<string>((id) => api.profiles.interview.discard(id), onError);
}

export function usePublishCvInterview() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<
    { profile: ProfileCv; interview: CvInterview },
    Error,
    {
      interviewId: string;
      publish: boolean;
      acceptSkills?: boolean;
      publishFacts?: boolean;
      publishRate?: boolean;
      applyToCv?: boolean;
    }
  >({
    mutationFn: (input) => api.profiles.interview.publish(input),
    onSuccess: (result) => {
      queryClient.setQueryData<ProfileCv>(queryKeys.profileOwn, result.profile);
      queryClient.setQueryData<CvInterviewState>(queryKeys.cvInterview, (previous) =>
        previous ? { ...previous, interview: result.interview } : previous,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.profileOwn });
    },
    onError,
  });
}

export function useProfileSearch(query: ProfileSearchQuery, enabled = true) {
  const api = useApi();
  return useQuery<ProfileSearchPage>({
    queryKey: queryKeys.profileSearch(query),
    queryFn: () => api.profiles.search(query),
    enabled,
  });
}

// ── Notifications ────────────────────────────────────────────────────────────────────────────

export function useNotifications(
  options: { archived?: boolean; offset?: number; limit?: number; popoverOpen?: boolean } = {},
) {
  const api = useApi();
  return useQuery<{
    pagination: { offset: number; limit: number; total: number };
    notifications: Notification[];
    unseen: number;
  }>({
    queryKey: queryKeys.notifications(options.archived, options.offset ?? 0),
    queryFn: () => api.notifications.list(options),
    // N12: there is no `notifications.update` broadcast in production, so a second tab never
    // learns something was read there — refetch on focus, and poll while the popover is open.
    refetchOnWindowFocus: true,
    refetchInterval: options.popoverOpen ? 60_000 : false,
  });
}

/** The bell's unread dot. ⚠ N6: must send no `eventType` — `unseen` is computed AFTER the
 * eventType/archived filters, so a filtered call would answer a wrong unread count. */
export function useUnreadCount() {
  const api = useApi();
  return useQuery<number>({
    queryKey: queryKeys.notificationsUnread,
    queryFn: async () => (await api.notifications.list({ archived: false, limit: 1 })).unseen,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

function invalidateNotifications(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["notifications"] });
}

export function useMarkNotificationRead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.notifications.markRead(id),
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

export function useArchiveNotification() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.notifications.archive(id),
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

export function useMarkAllNotificationsRead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<number, Error, void>({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: () => invalidateNotifications(queryClient),
  });
}

export function useNotificationPreferences() {
  const api = useApi();
  return useQuery<NotificationPreferences>({
    queryKey: queryKeys.notificationPreferences,
    queryFn: () => api.notifications.preferences(),
  });
}

export function useSetNotificationChannel() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<
    void,
    Error,
    { eventType: string; channel: NotificationChannel; enabled: boolean }
  >({
    mutationFn: (input) => api.notifications.setChannel(input),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationPreferences }),
    onError,
  });
}
// ── W8: the document AI layer ────────────────────────────────────────────────────────────────
// One namespace, two controllers, and no realtime yet: the drawer POLLS (§F.4 decision 1). A
// 5 s refetch while the drawer is on screen covers every `ConversationEvent` the SignalR hub
// would have delivered, and the SSE turn stream already covers the only thing that needs
// sub-second latency. React Query's `staleTime` is also what makes kms's D19 defect —
// cache-first with no TTL and no revalidation — impossible to reproduce here.

/** While the drawer is open the list and the transcript are worth re-asking for. */
export const CONVERSATION_POLL_MS = 5_000;

export function useDocumentConversations(
  documentId: string | null,
  search: ConversationSearch = {},
  options: { poll?: boolean } = {},
) {
  const api = useApi();
  return useQuery<DocConversationSummary[]>({
    queryKey: queryKeys.documentConversations(documentId ?? "", search),
    queryFn: () => api.conversations.forDocument(documentId ?? "", search),
    enabled: Boolean(documentId),
    staleTime: 2_000,
    refetchInterval: options.poll ? CONVERSATION_POLL_MS : false,
  });
}

/** The RAW read — control rows included. The transcript filters, it never re-fetches. */
export function useConversationDetail(id: string | null, options: { poll?: boolean } = {}) {
  const api = useApi();
  return useQuery<DocConversation>({
    queryKey: queryKeys.conversationDetail(id ?? ""),
    queryFn: () => api.conversations.detail(id ?? ""),
    enabled: Boolean(id),
    staleTime: 2_000,
    refetchInterval: options.poll ? CONVERSATION_POLL_MS : false,
  });
}

export function useRecentlyDeletedConversations(enabled = true) {
  const api = useApi();
  return useQuery<DocConversationSummary[]>({
    queryKey: queryKeys.conversationsRecentlyDeleted,
    queryFn: () => api.conversations.recentlyDeleted(),
    enabled,
  });
}

export function useConversationsOverview(limit = 20) {
  const api = useApi();
  return useQuery<ConversationsOverview>({
    queryKey: queryKeys.conversationsOverview(limit),
    queryFn: () => api.conversations.overview(limit),
  });
}

/** Every conversation write lands here: one root, invalidated once, no hand-rolled reducers. */
function useConversationMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  onDone?: (result: TResult, args: TArgs) => void,
) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: (result, args) => {
      void queryClient.invalidateQueries({ queryKey: CONVERSATION_ROOT });
      onDone?.(result, args);
    },
    onError,
  });
}

export function useStartConversation() {
  const api = useApi();
  return useConversationMutation((draft: StartConversationDraft) => api.conversations.start(draft));
}

export function useRenameConversation() {
  const api = useApi();
  return useConversationMutation(({ id, title }: { id: string; title: string }) =>
    api.conversations.rename(id, title),
  );
}

export function useArchiveConversation() {
  const api = useApi();
  return useConversationMutation((id: string) => api.conversations.archive(id));
}

/** Un-archiving is the OTHER controller's patch — there is no `unarchive` verb. */
export function useUnarchiveConversation() {
  const api = useApi();
  return useConversationMutation((id: string) =>
    api.conversations.update(id, { isResolved: false }),
  );
}

export function useDeleteConversation() {
  const api = useApi();
  return useConversationMutation((id: string) => api.conversations.remove(id));
}

/** W8-18: kms never re-added the row locally, so a restore looked like nothing happened. */
export function useRestoreConversation() {
  const api = useApi();
  return useConversationMutation((id: string) => api.conversations.restore(id));
}

export function useMergeConversations() {
  const api = useApi();
  return useConversationMutation(({ ids, title }: { ids: string[]; title?: string }) =>
    api.conversations.merge(ids, title),
  );
}

export function useTransformConversation() {
  const api = useApi();
  return useConversationMutation(
    ({ id, kind, asExport }: { id: string; kind: "Summarize" | "Simplify"; asExport?: boolean }) =>
      api.conversations.transform(id, kind, asExport),
  );
}

export function useRegenerateConversationSummary() {
  const api = useApi();
  return useConversationMutation((id: string) => api.conversations.regenerateSummary(id));
}

/**
 * Persist a row WITHOUT invoking a thinker (the lifecycle face's `POST .../{id}/messages`).
 *
 * S10's think thread needs this: a `config` row recording which thinkers were chosen, and a
 * `transform` row per AI result, are records — not turns. The turn face's `send` invokes up to
 * three thinkers inline for every message it receives, so writing a control row through it
 * would start a conversation nobody asked for.
 */
export function useAppendConversationMessage() {
  const api = useApi();
  return useConversationMutation(({ id, message }: { id: string; message: MessageDraft }) =>
    api.conversations.appendMessage(id, message),
  );
}

export function useSendConversationMessage() {
  const api = useApi();
  return useConversationMutation(({ id, message }: { id: string; message: MessageDraft }) =>
    api.conversations.send(id, message),
  );
}

export function useRunConversationPrompt() {
  const api = useApi();
  return useConversationMutation(({ id, run }: { id: string; run: PromptRunDraft }) =>
    api.conversations.prompt(id, run),
  );
}

export function useReviseMessage() {
  const api = useApi();
  return useConversationMutation(
    ({ messageId, patch }: { messageId: string; patch: RevisePatch }) =>
      api.conversations.revise(messageId, patch),
  );
}

export function useRegenerateConversation() {
  const api = useApi();
  return useConversationMutation(({ id, fromMessageId }: { id: string; fromMessageId?: string }) =>
    api.conversations.regenerate(id, fromMessageId),
  );
}

export function useRethinkMessage() {
  const api = useApi();
  return useConversationMutation(
    ({
      id,
      messageId,
      updatedContent,
    }: {
      id: string;
      messageId: string;
      updatedContent?: string;
    }) => api.conversations.rethink(id, messageId, updatedContent),
  );
}

export function useRestoreDroppedMessages() {
  const api = useApi();
  return useConversationMutation(({ id, messageIds }: { id: string; messageIds: string[] }) =>
    api.conversations.restoreDropped(id, messageIds),
  );
}

export function useCommitConversation() {
  const api = useApi();
  return useConversationMutation(({ id, title }: { id: string; title?: string }) =>
    api.conversations.commit(id, title === undefined ? {} : { title }),
  );
}

export function useReopenConversation() {
  const api = useApi();
  return useConversationMutation((id: string) => api.conversations.reopen(id));
}

export function useFlattenConversation() {
  const api = useApi();
  return useConversationMutation(
    ({ id, title, wrapWithHeading }: { id: string; title?: string; wrapWithHeading?: boolean }) =>
      api.conversations.flatten(id, { title, wrapWithHeading }),
  );
}

// ── the context pack (S6 builds the UI on these) ────────────────────────────

export function useConversationContext(id: string | null) {
  const api = useApi();
  return useQuery<ContextPack>({
    queryKey: queryKeys.conversationContext(id ?? ""),
    queryFn: () => api.conversations.context.list(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useConversationContextConfig(id: string | null) {
  const api = useApi();
  return useQuery<ContextConfig>({
    queryKey: queryKeys.conversationContextConfig(id ?? ""),
    queryFn: () => api.conversations.context.config(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useAddContextItem() {
  const api = useApi();
  return useConversationMutation(({ id, item }: { id: string; item: ContextItemDraft }) =>
    api.conversations.context.add(id, item),
  );
}

export function useUpdateContextItem() {
  const api = useApi();
  return useConversationMutation(
    ({ id, itemId, patch }: { id: string; itemId: string; patch: Partial<ContextItemDraft> }) =>
      api.conversations.context.update(id, itemId, patch),
  );
}

export function useRemoveContextItem() {
  const api = useApi();
  return useConversationMutation(({ id, itemId }: { id: string; itemId: string }) =>
    api.conversations.context.remove(id, itemId),
  );
}

export function useSetContextConfig() {
  const api = useApi();
  return useConversationMutation(
    ({ id, patch }: { id: string; patch: { status?: string; aggressiveness?: string } }) =>
      api.conversations.context.setConfig(id, patch),
  );
}

/** A MUTATION, not a query: `suggest` persists every suggestion before it answers. */
export function useSuggestContext() {
  const api = useApi();
  return useConversationMutation(
    ({ id, aggressiveness }: { id: string; aggressiveness?: string }) =>
      api.conversations.context.suggest(id, { aggressiveness }),
  );
}

// ── assigned thinkers (S5) ──────────────────────────────────────────────────

export function useConversationThinkers(id: string | null) {
  const api = useApi();
  return useQuery<string[]>({
    queryKey: queryKeys.conversationThinkers(id ?? ""),
    queryFn: () => api.conversations.thinkers.list(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useSetConversationThinkers() {
  const api = useApi();
  return useConversationMutation(({ id, thinkerIds }: { id: string; thinkerIds: string[] }) =>
    api.conversations.thinkers.set(id, thinkerIds),
  );
}

export function useAddConversationThinker() {
  const api = useApi();
  return useConversationMutation(({ id, thinkerId }: { id: string; thinkerId: string }) =>
    api.conversations.thinkers.add(id, thinkerId),
  );
}

/** ⚠ `action:"delete"` destroys that thinker's messages — the caller must confirm first. */
export function useRemoveConversationThinker() {
  const api = useApi();
  return useConversationMutation(
    ({
      id,
      thinkerId,
      disposition,
    }: {
      id: string;
      thinkerId: string;
      disposition: ThinkerDisposition;
    }) => api.conversations.thinkers.remove(id, thinkerId, disposition),
  );
}

// ── outcomes and BDI (S8 builds the UI on these) ────────────────────────────

export function useConversationOutcomes(id: string | null) {
  const api = useApi();
  return useQuery<Outcomes>({
    queryKey: queryKeys.conversationOutcomes(id ?? ""),
    queryFn: () => api.conversations.outcomes.get(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useConversationRuns(id: string | null) {
  const api = useApi();
  return useQuery<BdiRun[]>({
    queryKey: queryKeys.conversationRuns(id ?? ""),
    queryFn: () => api.conversations.outcomes.runs(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useConversationUsage(id: string | null) {
  const api = useApi();
  return useQuery<UsageRollup>({
    queryKey: queryKeys.conversationUsage(id ?? ""),
    queryFn: () => api.conversations.outcomes.usage(id ?? ""),
    enabled: Boolean(id),
  });
}

export function useCreateTruth() {
  const api = useApi();
  return useConversationMutation(({ id, draft }: { id: string; draft: TruthDraft }) =>
    api.conversations.outcomes.createTruth(id, draft),
  );
}

export function useSupersedeTruth() {
  const api = useApi();
  return useConversationMutation(
    ({ id, truthId, draft }: { id: string; truthId: string; draft: Partial<TruthDraft> }) =>
      api.conversations.outcomes.supersedeTruth(id, truthId, draft),
  );
}

export function useCreateDecision() {
  const api = useApi();
  return useConversationMutation(({ id, draft }: { id: string; draft: DecisionDraft }) =>
    api.conversations.outcomes.createDecision(id, draft),
  );
}

/** ⚠ pause/resume/cancel answer 200 for an unknown id — the invalidation re-reads the truth. */
export function useDecisionControl() {
  const api = useApi();
  return useConversationMutation(
    ({ id, decisionId, action }: { id: string; decisionId: string; action: DecisionAction }) =>
      api.conversations.outcomes.control(id, decisionId, action),
  );
}

// ── source traces (S7) ──────────────────────────────────────────────────────

export function useSourceTraces(documentId: string | null) {
  const api = useApi();
  return useQuery<SourceTrace[]>({
    queryKey: queryKeys.conversationSourceTraces(documentId ?? ""),
    queryFn: () => api.conversations.sourceTraces(documentId ?? ""),
    enabled: Boolean(documentId),
  });
}

// ── document AI settings (S5) ───────────────────────────────────────────────

export function useDocumentAiSettings(documentId: string | null) {
  const api = useApi();
  return useQuery<DocumentAiSettings>({
    queryKey: queryKeys.documentAiSettings(documentId ?? ""),
    queryFn: () => api.conversations.documentAi.settings(documentId ?? ""),
    enabled: Boolean(documentId),
  });
}

export function useUpdateDocumentAiSettings() {
  const api = useApi();
  return useConversationMutation(
    ({
      documentId,
      patch,
    }: {
      documentId: string;
      patch: { aiAssistanceEnabled?: boolean; defaultContextItemIds?: string[] };
    }) => api.conversations.documentAi.setSettings(documentId, patch),
  );
}

export function useAddDocumentThinker() {
  const api = useApi();
  return useConversationMutation(
    ({ documentId, thinkerId }: { documentId: string; thinkerId: string }) =>
      api.conversations.documentAi.addThinker(documentId, thinkerId),
  );
}

export function useRemoveDocumentThinker() {
  const api = useApi();
  return useConversationMutation(
    ({ documentId, thinkerId }: { documentId: string; thinkerId: string }) =>
      api.conversations.documentAi.removeThinker(documentId, thinkerId),
  );
}

/** Who answers selection actions and transforms outside a conversation, and WHY. */
export function useDocumentThinker(input: { documentId?: string; collectionId?: string }) {
  const api = useApi();
  return useQuery<DocumentThinkerResolution>({
    queryKey: queryKeys.documentThinker(input.documentId ?? "", input.collectionId ?? ""),
    queryFn: () => api.conversations.documentAi.resolved(input),
    enabled: Boolean(input.documentId || input.collectionId),
  });
}

export function usePinDocumentThinker() {
  const api = useApi();
  return useConversationMutation(
    (draft: {
      thinkerId: string;
      collectionId?: string;
      scope?: "collection" | "user";
      documentId?: string;
      machineId?: string;
      workspacePath?: string;
    }) => api.conversations.documentAi.pin(draft),
  );
}

// ── the document chat (S9) ──────────────────────────────────────────────────
// No hub yet (§F.4 decision 1): a 4 s poll while the panel is on screen covers every
// `DocumentChatMessage` the CollaborationHub would have pushed, and the AI's reply arrives on
// the same channel. The socket, when it lands, is a strict enhancement with the same handlers.

export const DOCUMENT_CHAT_POLL_MS = 4_000;

export function useDocumentChat(documentId: string | null, options: { poll?: boolean } = {}) {
  const api = useApi();
  return useQuery<DocumentChatMessage[]>({
    queryKey: queryKeys.documentChat(documentId ?? ""),
    queryFn: () => api.documentChat.list(documentId ?? ""),
    enabled: Boolean(documentId),
    staleTime: 2_000,
    refetchInterval: options.poll ? DOCUMENT_CHAT_POLL_MS : false,
  });
}

export function useSendDocumentChat() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation<DocumentChatMessage, Error, { documentId: string; text: string }>({
    mutationFn: ({ documentId, text }) => api.documentChat.send(documentId, text),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: DOCUMENT_CHAT_ROOT }),
    onError,
  });
}

// ── comments (S10) ──────────────────────────────────────────────────────────
// Their own root, invalidated once per write. A think thread ALSO writes conversation
// messages, so those mutations keep using the conversation hooks — the two roots stay apart
// on purpose: a transform reply landing must not re-fetch every comment on the document.

export function useComments(query: CommentListQuery, enabled = true) {
  const api = useApi();
  return useQuery<CommentPage>({
    queryKey: queryKeys.comments(query),
    queryFn: () => api.comments.list(query),
    enabled: enabled && Boolean(query.documentId || query.parentCommentId || query.collectionId),
    staleTime: 2_000,
  });
}

/** Derived, not read: the server's cached `reactions` column is always empty (W8-5). */
export function useCommentReactions(commentId: string | null) {
  const api = useApi();
  return useQuery<ReactionSummary[]>({
    queryKey: queryKeys.commentReactions(commentId ?? ""),
    queryFn: () => api.comments.reactions(commentId ?? ""),
    enabled: Boolean(commentId),
  });
}

function useCommentMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COMMENT_ROOT }),
    onError,
  });
}

export function useCreateComment() {
  const api = useApi();
  return useCommentMutation((draft: CommentDraft) => api.comments.create(draft));
}

export function useUpdateComment() {
  const api = useApi();
  return useCommentMutation(({ id, text }: { id: string; text: string }) =>
    api.comments.update(id, text),
  );
}

export function useDeleteComment() {
  const api = useApi();
  return useCommentMutation((id: string) => api.comments.remove(id));
}

/** ⚠ Refuses for a reply, server-side and in the fixture: a thread resolves, a reply cannot. */
export function useResolveComment() {
  const api = useApi();
  return useCommentMutation(({ id, resolved }: { id: string; resolved: boolean }) =>
    resolved ? api.comments.resolve(id) : api.comments.unresolve(id),
  );
}

export function useToggleCommentReaction() {
  const api = useApi();
  return useCommentMutation(({ id, emoji, on }: { id: string; emoji: string; on: boolean }) =>
    on ? api.comments.addReaction(id, emoji) : api.comments.removeReaction(id, emoji),
  );
}

export type { Comment };

// ── Git connections (2026-09-24) ───────────────────────────────────────────────

/**
 * The workspace's connections, one project's, or (`scope: "workspace"`) only the workspace-level
 * ones. The sidebar reads all three shapes; one cache key per scope keeps them apart.
 */
export function useGitConnections(query: { projectId?: string; scope?: "workspace" } = {}) {
  const api = useApi();
  const scope = query.projectId ? `project:${query.projectId}` : (query.scope ?? "all");
  return useQuery<GitConnectionList>({
    queryKey: queryKeys.gitConnections(scope),
    queryFn: () => api.git.connections.list(query),
    staleTime: 60_000,
  });
}

export function useCreateGitConnection() {
  const api = useApi();
  return useAutomationMutation(
    (input: GitConnectionDraft): Promise<GitConnection> => api.git.connections.create(input),
    GIT_ROOT,
  );
}

export function useUpdateGitConnection() {
  const api = useApi();
  return useAutomationMutation(
    (input: GitConnectionPatch): Promise<GitConnection> => api.git.connections.update(input),
    GIT_ROOT,
  );
}

export function useRemoveGitConnection() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.git.connections.remove(id), GIT_ROOT);
}

export function useRotateGitConnection() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, token }: { id: string; token: string }): Promise<GitConnection> =>
      api.git.connections.rotate(id, token),
    GIT_ROOT,
  );
}

/** Asks the provider; nothing is cached — the answer is only as good as the moment it was asked. */
export function useTestGitConnection() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: string): Promise<GitProbe> => api.git.connections.test(id),
    onError,
  });
}

export function useIngestGitConnection() {
  const api = useApi();
  return useAutomationMutation(
    ({ id, sinceDays }: { id: string; sinceDays?: number }): Promise<GitIngestReport> =>
      api.git.connections.ingest(id, sinceDays),
    GIT_ROOT,
  );
}

/**
 * One level of a repository, fetched when disclosed — the child list only mounts expanded, so
 * a closed folder never costs a call (the `CollectionTree` discipline).
 */
export function useGitTree(id: string, path = "", enabled = true) {
  const api = useApi();
  return useQuery<GitTree>({
    queryKey: queryKeys.gitTree(id, path),
    queryFn: () => api.git.connections.tree(id, path),
    enabled: enabled && Boolean(id),
    staleTime: 5 * 60_000,
  });
}

export function useGitFile(id: string, path: string) {
  const api = useApi();
  return useQuery<GitFile>({
    queryKey: queryKeys.gitFile(id, path),
    queryFn: () => api.git.connections.file(id, path),
    enabled: Boolean(id) && Boolean(path),
    staleTime: 5 * 60_000,
  });
}

// ── Mail connections (2026-10-16): the person's own Gmail / Outlook mailbox ─────────────────

const MAIL_ROOT = ["mail"] as const;

export function useMailProviders() {
  const api = useApi();
  return useQuery<MailProviders>({
    queryKey: ["mail", "providers"],
    queryFn: () => api.mail.providers(),
    staleTime: 5 * 60_000,
  });
}

export function useMailConnections() {
  const api = useApi();
  return useQuery<MailConnection[]>({
    queryKey: ["mail", "connections"],
    queryFn: () => api.mail.connections.list(),
    staleTime: 30_000,
  });
}

/** Leaves the app for the provider's consent page; it returns to `returnTo` with the outcome. */
export function useStartMailConnect() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: async ({
      provider,
      returnTo,
    }: {
      provider: MailProviderName;
      returnTo: string;
    }) => {
      const url = await api.mail.connections.connectStart(provider, returnTo);
      leaveApp(url);
    },
    onError,
  });
}

export function useUpdateMailConnection() {
  const api = useApi();
  return useAutomationMutation(
    (input: MailConnectionPatch): Promise<MailConnection> => api.mail.connections.update(input),
    MAIL_ROOT,
  );
}

export function useSyncMailConnection() {
  const api = useApi();
  return useAutomationMutation(
    (id: string): Promise<MailSyncReport> => api.mail.connections.sync(id),
    MAIL_ROOT,
  );
}

export function useRemoveMailConnection() {
  const api = useApi();
  return useAutomationMutation((id: string) => api.mail.connections.remove(id), MAIL_ROOT);
}

/** Read live from the provider — nothing of the inbox is stored. */
export function useMailInbox(id: string, enabled = true) {
  const api = useApi();
  return useQuery<MailInboxMessage[]>({
    queryKey: ["mail", "inbox", id],
    queryFn: () => api.mail.connections.inbox(id, 20),
    enabled: enabled && Boolean(id),
    staleTime: 60_000,
    retry: false,
  });
}

export function useMailActivity(id: string, enabled = true) {
  const api = useApi();
  return useQuery<MailActivity[]>({
    queryKey: ["mail", "activity", id],
    queryFn: () => api.mail.connections.activity(id, 30),
    enabled: enabled && Boolean(id),
    staleTime: 30_000,
  });
}

export function useImportMail() {
  const api = useApi();
  return useAutomationMutation(
    ({
      id,
      messageId,
      as,
      collectionId,
      projectId,
    }: {
      id: string;
      messageId: string;
      as: MailImportAs;
      collectionId?: string;
      projectId?: string;
    }): Promise<MailImportResult> =>
      api.mail.connections.import(id, messageId, as, { collectionId, projectId }),
    MAIL_ROOT,
  );
}

export function useSendMail() {
  const api = useApi();
  return useAutomationMutation((input: MailSendInput) => api.mail.send(input), MAIL_ROOT);
}

// ── GitHub App installations (2026-09-24): installed on GitHub, proven, then picked ─────────

export function useGitHubAppStatus() {
  const api = useApi();
  return useQuery<GitHubAppStatus>({
    queryKey: ["git", "github", "status"],
    queryFn: () => api.git.github.status(),
    staleTime: 5 * 60_000,
  });
}

export function useGitHubInstallations(enabled = true) {
  const api = useApi();
  return useQuery<GitHubInstallation[]>({
    queryKey: ["git", "github", "installations"],
    queryFn: () => api.git.github.installations(),
    enabled,
  });
}

export function useGitHubRepositories(installationId: string) {
  const api = useApi();
  return useQuery<GitHubRepository[]>({
    queryKey: ["git", "github", "repositories", installationId],
    queryFn: () => api.git.github.repositories(installationId),
    enabled: Boolean(installationId),
    staleTime: 60_000,
  });
}

/** Leaves the app for GitHub's install page; GitHub returns to `returnTo` with the outcome. */
export function useStartGitHubInstall() {
  const api = useApi();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: async ({ returnTo, link }: { returnTo: string; link?: boolean }) => {
      const url = await api.git.github.installStart(returnTo, link);
      leaveApp(url);
    },
    onError,
  });
}
